// ─────────────────────────────────────────────────────────────────────────────
//  Bazaar / Classified — co-located page components (pattern mirrors AvailableLoadsPage.tsx)
//    BazaarBrowsePage           — browse/search Bazaar (Sell/Buy) listings
//    ClassifiedsBrowsePage      — browse/search Classified ads
//    BazaarListingDetailPage    — Bazaar listing detail, submit quote
//    ClassifiedListingDetailPage— Classified ad detail, contact seller
//    CreateListingPage          — post a new Bazaar listing or Classified ad
//    MyBazaarPage               — my Bazaar listings + my orders
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ShoppingCart, Tag, Search, Plus, X, CheckCircle, Heart,
  Package, Truck, MapPin, Star, Filter, ArrowLeft, Navigation, Sparkles,
  MessageCircle, Store, Boxes,
} from 'lucide-react';
import { useAppAuth } from '../hooks/useAppAuth';
import { useDeliveryEstimate } from '../hooks/useDeliveryEstimate';
import {
  createApiClient, bazaarService, classifiedService,
  publicBazaarService, publicClassifiedService, profileService,
  bazaarOrdersService, bazaarCategoryService, bazaarReviewService,
  stripeConnectApi, bazaarShipmentsService,
} from '../services/api';
import { geocodePostcode } from '../lib/geocoding';
import { PostcodeInput } from '../components/PostcodeInput';
import { MediaUploader } from '../components/MediaUploader';
import { BazaarChatPanel } from '../components/BazaarChatPanel';
import { ListingAttributeFields, toAttributeValues, countRemaining } from '../components/ListingAttributeFields';
import {
  ListingAttributeFilters, carryOverFilters, encodeAttributeFilters,
} from '../components/ListingAttributeFilters';
import { useBazaarCartStore } from '../stores/bazaarCartStore';
import {
  BazaarListingType, ListingStatus,
  BazaarPricingType, DeliveryOption,
  BazaarOrderStatus, BazaarShipmentStatus, BazaarFulfilmentMethod,
  type BazaarListing, type BazaarSearchParams,
  type CreateBazaarListingInput, type BazaarOrder, type BazaarOrderItem,
  type BazaarFavouriteSummary, type ListingMedia,
  type ClassifiedListing, type ClassifiedSearchParams,
  type CreateClassifiedListingInput, type ClassifiedFavouriteSummary,
  type ListingAttributeFilter, type ListingCategoryAttribute,
  type ShippingAddressInput, type ShippingRateQuote,
} from '../types';

const SHIPMENT_STATUS_LABEL: Record<BazaarShipmentStatus, string> = {
  [BazaarShipmentStatus.AwaitingRate]:   'Not shipped yet',
  [BazaarShipmentStatus.RatesFetched]:   'Rates fetched',
  [BazaarShipmentStatus.LabelPurchased]: 'Label purchased',
  [BazaarShipmentStatus.Voided]:         'Label voided',
  [BazaarShipmentStatus.InTransit]:      'In transit',
  [BazaarShipmentStatus.Delivered]:      'Delivered',
  [BazaarShipmentStatus.Exception]:      'Delivery exception',
};

const EMPTY_SHIP_ADDRESS: ShippingAddressInput = {
  name: '', phone: '', addressLine1: '', cityLocality: '', postalCode: '', countryCode: 'GB',
};

/**
 * Shipping for one Bazaar order item — ShipEngine rate shopping, label purchase/void, and
 * tracking. Sellers get full control; buyers get a read-only tracking view. Self-contained
 * (fetches its own shipment query) so it drops into both the "sales" and "orders" tabs below.
 */
function BazaarShipmentPanel({ orderId, itemId, role }: { orderId: string; itemId: string; role: 'seller' | 'buyer' }) {
  const { getToken } = useAppAuth();
  const qc = useQueryClient();
  const svc = bazaarShipmentsService(createApiClient(getToken));

  const [showForm, setShowForm] = useState(false);
  const [shipFrom, setShipFrom] = useState<ShippingAddressInput>(EMPTY_SHIP_ADDRESS);
  const [shipTo, setShipTo] = useState<ShippingAddressInput>(EMPTY_SHIP_ADDRESS);
  const [rates, setRates] = useState<ShippingRateQuote[] | null>(null);
  const [selectedRateId, setSelectedRateId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data: shipment } = useQuery({
    queryKey: ['bazaar-shipment', itemId],
    queryFn: () => svc.get(orderId, itemId),
  });

  const ratesMutation = useMutation({
    mutationFn: () => svc.getRates(orderId, itemId, shipFrom, shipTo),
    onSuccess: (result) => { setRates(result); setError(null); },
    onError: (e: any) => setError(e?.response?.data?.error ?? e?.message ?? 'Could not fetch shipping rates.'),
  });

  const labelMutation = useMutation({
    mutationFn: () => svc.purchaseLabel(orderId, itemId, selectedRateId!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bazaar-shipment', itemId] });
      setShowForm(false);
      setRates(null);
      setError(null);
    },
    onError: (e: any) => setError(e?.response?.data?.error ?? e?.message ?? 'Could not buy the shipping label.'),
  });

  const voidMutation = useMutation({
    mutationFn: () => svc.voidLabel(orderId, itemId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bazaar-shipment', itemId] }),
    onError: (e: any) => setError(e?.response?.data?.error ?? e?.message ?? 'Could not void the shipping label.'),
  });

  const hasLabel = shipment && shipment.status >= BazaarShipmentStatus.LabelPurchased && shipment.status !== BazaarShipmentStatus.Voided;

  if (role === 'buyer') {
    if (!shipment || !hasLabel) return null;
    return (
      <div data-testid={`shipment-tracking-${itemId}`} className="mt-2 pt-2 border-t border-gray-100 text-xs text-gray-600">
        <p>Carrier: <span className="font-semibold text-gray-900">{shipment.carrierCode}</span></p>
        <p>Tracking: <span data-testid={`tracking-number-${itemId}`} className="font-semibold text-gray-900">{shipment.trackingNumber}</span></p>
        <p>Status: {SHIPMENT_STATUS_LABEL[shipment.status]}</p>
      </div>
    );
  }

  // Seller view.
  return (
    <div className="mt-2 pt-2 border-t border-gray-100">
      {hasLabel ? (
        <div data-testid={`shipment-tracking-${itemId}`} className="text-xs text-gray-600 space-y-1">
          <p>Carrier: <span className="font-semibold text-gray-900">{shipment!.carrierCode}</span></p>
          <p>Tracking: <span data-testid={`tracking-number-${itemId}`} className="font-semibold text-gray-900">{shipment!.trackingNumber}</span></p>
          <p>Status: {SHIPMENT_STATUS_LABEL[shipment!.status]}</p>
          <div className="flex gap-2 mt-1">
            {shipment!.labelPdfUrl && (
              <a href={shipment!.labelPdfUrl} target="_blank" rel="noreferrer"
                 className="px-2 py-1 font-semibold text-blue-700 border border-blue-200 bg-blue-50 rounded-lg hover:bg-blue-100">
                Download label
              </a>
            )}
            <button
              data-testid={`void-shipping-label-${itemId}`}
              onClick={() => voidMutation.mutate()}
              disabled={voidMutation.isPending}
              className="px-2 py-1 font-semibold text-red-600 border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-50"
            >
              Void label
            </button>
          </div>
          {error && <p className="text-red-600">{error}</p>}
        </div>
      ) : !showForm ? (
        <button
          data-testid={`get-shipping-rates-${itemId}`}
          onClick={() => setShowForm(true)}
          className="w-full px-3 py-1.5 text-xs font-semibold text-blue-700 border border-blue-200 bg-blue-50 rounded-lg hover:bg-blue-100"
        >
          Get shipping rates
        </button>
      ) : (
        <div className="space-y-2 text-xs">
          <div className="grid grid-cols-2 gap-2">
            <input data-testid={`ship-from-postcode-${itemId}`} placeholder="Your postcode" value={shipFrom.postalCode}
              onChange={e => setShipFrom(prev => ({ ...prev, postalCode: e.target.value }))}
              className="border border-gray-300 rounded-lg px-2 py-1.5" />
            <input data-testid={`ship-to-postcode-${itemId}`} placeholder="Buyer's postcode" value={shipTo.postalCode}
              onChange={e => setShipTo(prev => ({ ...prev, postalCode: e.target.value }))}
              className="border border-gray-300 rounded-lg px-2 py-1.5" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <input placeholder="Your name/phone combined for brevity — split if the UI needs it" value={shipFrom.name}
              onChange={e => setShipFrom(prev => ({ ...prev, name: e.target.value, phone: prev.phone || '00000000000' }))}
              className="border border-gray-300 rounded-lg px-2 py-1.5" />
            <input placeholder="Buyer's name" value={shipTo.name}
              onChange={e => setShipTo(prev => ({ ...prev, name: e.target.value, phone: prev.phone || '00000000000' }))}
              className="border border-gray-300 rounded-lg px-2 py-1.5" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <input placeholder="Your address + city" value={shipFrom.addressLine1}
              onChange={e => setShipFrom(prev => ({ ...prev, addressLine1: e.target.value, cityLocality: e.target.value }))}
              className="border border-gray-300 rounded-lg px-2 py-1.5" />
            <input placeholder="Buyer's address + city" value={shipTo.addressLine1}
              onChange={e => setShipTo(prev => ({ ...prev, addressLine1: e.target.value, cityLocality: e.target.value }))}
              className="border border-gray-300 rounded-lg px-2 py-1.5" />
          </div>
          <button
            data-testid={`fetch-rates-button-${itemId}`}
            onClick={() => ratesMutation.mutate()}
            disabled={ratesMutation.isPending}
            className="w-full px-3 py-1.5 font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {ratesMutation.isPending ? 'Fetching rates…' : 'Fetch rates'}
          </button>

          {rates && rates.length > 0 && (
            <div className="space-y-1">
              {rates.map((r, i) => (
                <label key={r.rateId} data-testid={`rate-option-${i}`} className="flex items-center gap-2 border border-gray-200 rounded-lg px-2 py-1.5 cursor-pointer">
                  <input type="radio" name={`rate-${itemId}`} checked={selectedRateId === r.rateId}
                    onChange={() => setSelectedRateId(r.rateId)} />
                  <span className="flex-1">{r.carrierCode} — {r.serviceName}</span>
                  <span className="font-semibold">£{r.amount.toFixed(2)}</span>
                </label>
              ))}
              <button
                data-testid={`buy-label-button-${itemId}`}
                onClick={() => labelMutation.mutate()}
                disabled={!selectedRateId || labelMutation.isPending}
                className="w-full px-3 py-1.5 font-semibold text-white bg-emerald-700 rounded-lg hover:bg-emerald-800 disabled:opacity-50"
              >
                {labelMutation.isPending ? 'Buying label…' : 'Buy label'}
              </button>
            </div>
          )}

          {rates && rates.length === 0 && <p className="text-gray-500">No rates available for these addresses.</p>}
          {error && <p className="text-red-600">{error}</p>}
        </div>
      )}
    </div>
  );
}

/**
 * Wraps BazaarShipmentPanel with the seller's explicit fulfilment-method choice: Free Shipping
 * (seller pays, may still buy a label via ShipEngine), Buyer Pays (ShipEngine-calculated
 * shipping owed by the buyer), Free Collection, or a TransitDeal delivery job for medium/large
 * items. The backend only allows ShipEngine rate-shopping once the method is FreeShipping or
 * BuyerPaysShipEngine (BazaarShipmentService.RequireShipEngineFulfilmentMethod), and only the
 * seller may set/change it (IBazaarOrderService.SetFulfilmentMethodAsync).
 *
 * Known limitation: BuyerPaysShipEngine records the ShipEngine quote (BazaarShipment.rateAmount)
 * and displays it as owed, but nothing here actually bills the buyer for it — that needs its own
 * payment-capture design (a new charge, or folding it into the existing COD amount) and is
 * deliberately left as a follow-up rather than guessed at here.
 */
function BazaarFulfilmentSection({
  order, item, role,
}: {
  order: BazaarOrder;
  item: BazaarOrderItem;
  role: 'seller' | 'buyer';
}) {
  const { getToken } = useAppAuth();
  const qc = useQueryClient();
  const ordersSvc = bazaarOrdersService(createApiClient(getToken));
  const [showPicker, setShowPicker] = useState(false);
  const [jobIdInput, setJobIdInput] = useState('');
  const [linkError, setLinkError] = useState<string | null>(null);

  const invalidateOrders = () => {
    qc.invalidateQueries({ queryKey: ['bazaar', 'orders', 'mine'] });
    setShowPicker(false);
  };

  const setMethodMutation = useMutation({
    mutationFn: (method: BazaarFulfilmentMethod) => ordersSvc.setFulfilmentMethod(order.id, item.id, method),
    onSuccess: invalidateOrders,
  });

  const linkJobMutation = useMutation({
    mutationFn: (jobId: string) => ordersSvc.linkDeliveryJob(order.id, item.id, jobId),
    onSuccess: () => {
      invalidateOrders();
      setLinkError(null);
    },
    onError: (e: any) => setLinkError(e?.response?.data?.error ?? e?.message ?? 'Could not link that delivery job.'),
  });

  const method = item.fulfilmentMethod;
  // Collection is a checkout-time choice (the buyer picked it to lock in the deposit/COD split —
  // see BazaarOrderService.CreateOrderAsync's RequiresDelivery handling) and can never be
  // reached or left from here; the seller only ever narrows a delivery item down to one of the
  // three ShipEngine/TransitDeal options below.
  const canChange = method === BazaarFulfilmentMethod.Unset
    || (method === BazaarFulfilmentMethod.TransitDealJob && !item.linkedDeliveryJobId);
    // FreeShipping/BuyerPaysShipEngine's "can change" additionally depends on the shipment's
    // label status, which this component doesn't load for the Unset/other branches —
    // BazaarShipmentPanel renders its own "Change" affordance once that data is available
    // (see its hasLabel check).

  if (role === 'seller' && (method === BazaarFulfilmentMethod.Unset || showPicker)) {
    return (
      <div className="mt-2 pt-2 border-t border-gray-100">
        <p className="text-xs font-semibold text-gray-700 mb-1.5">How will this item reach the buyer?</p>
        <div className="grid grid-cols-2 gap-1.5 text-xs">
          <button
            data-testid={`fulfilment-freeshipping-${item.id}`}
            onClick={() => setMethodMutation.mutate(BazaarFulfilmentMethod.FreeShipping)}
            disabled={setMethodMutation.isPending}
            className="px-2 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            Free shipping (I pay)
          </button>
          <button
            data-testid={`fulfilment-buyerpays-${item.id}`}
            onClick={() => setMethodMutation.mutate(BazaarFulfilmentMethod.BuyerPaysShipEngine)}
            disabled={setMethodMutation.isPending}
            className="px-2 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            Buyer pays (ShipEngine)
          </button>
          <button
            data-testid={`fulfilment-transitdealjob-${item.id}`}
            onClick={() => setMethodMutation.mutate(BazaarFulfilmentMethod.TransitDealJob)}
            disabled={setMethodMutation.isPending}
            className="px-2 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            TransitDeal delivery
          </button>
        </div>
        {setMethodMutation.isError && (
          <p className="text-xs text-red-600 mt-1">
            {(setMethodMutation.error as any)?.response?.data?.error ?? 'Could not set the fulfilment method.'}
          </p>
        )}
      </div>
    );
  }

  if (method === BazaarFulfilmentMethod.FreeShipping || method === BazaarFulfilmentMethod.BuyerPaysShipEngine) {
    const isBuyerPays = method === BazaarFulfilmentMethod.BuyerPaysShipEngine;
    return (
      <div>
        <div data-testid={`fulfilment-status-${item.id}`} className="mt-2 pt-2 border-t border-gray-100 flex items-center justify-between text-xs">
          <span className={`font-semibold ${isBuyerPays ? 'text-amber-700' : 'text-emerald-700'}`}>
            {isBuyerPays ? 'Buyer pays — ShipEngine-calculated shipping.' : 'Free shipping — included in the price.'}
          </span>
          {role === 'seller' && (
            <button data-testid={`fulfilment-change-${item.id}`} onClick={() => setShowPicker(true)} className="text-blue-600 hover:underline">Change</button>
          )}
        </div>
        {/* Both methods use the same ShipEngine rate/label/tracking flow — only who's expected
            to cover the quoted cost differs (BazaarShipmentService's
            RequireShipEngineFulfilmentMethod allows both). */}
        <BazaarShipmentPanel orderId={order.id} itemId={item.id} role={role} />
      </div>
    );
  }

  if (method === BazaarFulfilmentMethod.Collection) {
    // No "Change" here — the buyer locked this in at checkout (it decided the deposit/COD
    // split), and the backend rejects any attempt to move a Collection item to a delivery method.
    return (
      <div data-testid={`fulfilment-status-${item.id}`} className="mt-2 pt-2 border-t border-gray-100 text-xs">
        <span className="font-semibold text-gray-700">Free collection — the buyer will collect this in person.</span>
      </div>
    );
  }

  // TransitDealJob
  return (
    <div data-testid={`fulfilment-status-${item.id}`} className="mt-2 pt-2 border-t border-gray-100 text-xs space-y-1">
      {item.linkedDeliveryJobId ? (
        <span className="font-semibold text-emerald-700">Delivery job linked ✓</span>
      ) : role === 'buyer' ? (
        <div className="space-y-1.5">
          <p className="text-gray-700">This seller wants you to arrange a TransitDeal delivery job.</p>
          <Link
            data-testid={`fulfilment-post-job-link-${item.id}`}
            to={`/post-job?${new URLSearchParams({ description: `Delivery of: ${item.listingTitle}` })}`}
            target="_blank"
            className="inline-block px-2 py-1 text-blue-700 border border-blue-200 bg-blue-50 rounded-lg hover:bg-blue-100"
          >
            Post a delivery job
          </Link>
          <div className="flex gap-1.5">
            <input
              data-testid={`fulfilment-job-id-input-${item.id}`}
              value={jobIdInput}
              onChange={e => setJobIdInput(e.target.value)}
              placeholder="Paste the posted job's ID"
              className="flex-1 min-w-0 border border-gray-300 rounded-lg px-2 py-1"
            />
            <button
              data-testid={`fulfilment-link-job-button-${item.id}`}
              onClick={() => linkJobMutation.mutate(jobIdInput)}
              disabled={!jobIdInput || linkJobMutation.isPending}
              className="shrink-0 px-2 py-1 font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              Link job
            </button>
          </div>
          {linkError && <p className="text-red-600">{linkError}</p>}
        </div>
      ) : (
        <span className="text-gray-700">Waiting for the buyer to arrange a delivery job.</span>
      )}
      {role === 'seller' && canChange && (
        <button data-testid={`fulfilment-change-${item.id}`} onClick={() => setShowPicker(true)} className="text-blue-600 hover:underline">Change</button>
      )}
    </div>
  );
}

// 'nationwide' means no distance filter at all — the search runs across the whole country.
const RADIUS_OPTIONS: Array<number | 'nationwide'> = [5, 10, 25, 50, 100, 200, 'nationwide'];

const SORT_OPTIONS: { value: NonNullable<BazaarSearchParams['sortBy']>; label: string }[] = [
  { value: 'nearest',    label: 'Nearest first' },
  { value: 'newest',     label: 'Newest first' },
  { value: 'price_asc',  label: 'Price: low to high' },
  { value: 'price_desc', label: 'Price: high to low' },
];

// Favourite summaries carry only the fields needed for the card — fill in the rest of
// BazaarListing with harmless defaults so the existing ListingCard can render either.
function bazaarFavouriteToListing(f: BazaarFavouriteSummary): BazaarListing {
  return {
    id: f.listingId,
    listingType: f.listingType,
    status: f.status,
    sellerId: f.sellerId,
    sellerName: '',
    sellerVerified: false,
    title: f.title,
    description: f.description,
    category: f.category,
    intentionPrice: f.intentionPrice,
    pricingType: BazaarPricingType.Closed,
    deliveryOption: DeliveryOption.NotApplicable,
    location: f.location,
    media: f.media,
    quoteCount: 0,
    createdAt: f.createdAt,
    isFavourited: true,
    favouriteId: f.favouriteId,
  };
}

function classifiedFavouriteToListing(f: ClassifiedFavouriteSummary): ClassifiedListing {
  return {
    id: f.listingId,
    status: f.status,
    sellerId: f.sellerId,
    sellerName: '',
    sellerVerified: false,
    title: f.title,
    description: f.description,
    category: f.category,
    intentionPrice: f.intentionPrice,
    deliveryOption: DeliveryOption.NotApplicable,
    location: f.location,
    media: f.media,
    createdAt: f.createdAt,
    isFavourited: true,
    favouriteId: f.favouriteId,
  };
}

// ── Shared helpers ────────────────────────────────────────────────────────────

const STATUS_COLORS: Record<ListingStatus, string> = {
  [ListingStatus.Draft]:           'bg-gray-100 text-gray-700',
  [ListingStatus.Active]:          'bg-green-100 text-green-800',
  [ListingStatus.Sold]:            'bg-purple-100 text-purple-800',
  [ListingStatus.Fulfilled]:       'bg-teal-100 text-teal-800',
  [ListingStatus.Expired]:         'bg-gray-100 text-gray-500',
  [ListingStatus.Cancelled]:       'bg-red-100 text-red-700',
  [ListingStatus.PendingApproval]: 'bg-amber-100 text-amber-800',
  [ListingStatus.PendingPayment]:  'bg-amber-100 text-amber-800',
};

const STATUS_LABELS: Record<ListingStatus, string> = {
  [ListingStatus.Draft]:           'Draft',
  [ListingStatus.Active]:          'Active',
  [ListingStatus.Sold]:            'Sold',
  [ListingStatus.Fulfilled]:       'Fulfilled',
  [ListingStatus.Expired]:         'Expired',
  [ListingStatus.Cancelled]:       'Cancelled',
  [ListingStatus.PendingApproval]: 'Pending Review',
  [ListingStatus.PendingPayment]:  'Payment Pending',
};

const ORDER_STATUS_LABELS: Record<BazaarOrderStatus, string> = {
  [BazaarOrderStatus.PendingDeposit]: 'Awaiting Deposit',
  [BazaarOrderStatus.DepositPaid]:    'Deposit Paid',
  [BazaarOrderStatus.InProgress]:     'In Progress',
  [BazaarOrderStatus.AwaitingCod]:    'Awaiting COD',
  [BazaarOrderStatus.Completed]:      'Completed',
  [BazaarOrderStatus.Disputed]:       'Disputed',
  [BazaarOrderStatus.Cancelled]:      'Cancelled',
  [BazaarOrderStatus.Refunded]:       'Refunded',
};

// Fallback only. The real list is the taxonomy from bazaarCategoryService.getTree(), which
// is what every screen renders - a hardcoded copy drifts the moment operations add a
// category, and worse, a category missing from here became unselectable even though it was
// seeded with attributes (Property was, for exactly that reason).
const FALLBACK_CATEGORIES = [
  'Vehicles & Parts', 'Tools & Equipment', 'Electronics', 'Furniture',
  'Clothing', 'Books & Media', 'Garden & Outdoor', 'Services', 'Other',
];

/**
 * Sort options for a category's own attributes: a low-to-high and high-to-low pair for every
 * attribute the category marks IsSortable. "Mileage: Low to High" for vehicles, "Screen size:
 * High to Low" for TVs — nothing hardcoded per category, so a new sortable attribute reaches
 * users without a client release. sortAttrKey rides alongside sortBy because the search
 * procedure needs to know WHICH attribute to order on.
 */
function attributeSortOptions(definitions: ListingCategoryAttribute[]) {
  return definitions.filter(d => d.isSortable).flatMap(d => [
    { sortBy: 'attr_asc'  as const, attrKey: d.attrKey, label: `${d.label}: Low to High` },
    { sortBy: 'attr_desc' as const, attrKey: d.attrKey, label: `${d.label}: High to Low` },
  ]);
}

/** Top-level category names from the taxonomy, in its own display order. */
function topLevelNames(categories: { parentId: string | null; name: string }[] | undefined) {
  if (!categories?.length) return FALLBACK_CATEGORIES;
  return categories.filter(c => !c.parentId).map(c => c.name);
}

function ListingCard({ listing, kind, canFavourite, onLoginRequired }: {
  listing: BazaarListing | ClassifiedListing;
  kind: 'bazaar' | 'classified';
  canFavourite: boolean;
  onLoginRequired: () => void;
}) {
  const img = listing.media[0]?.url;
  const quoteCount = kind === 'bazaar' ? (listing as BazaarListing).quoteCount : 0;
  const href = kind === 'bazaar' ? `/bazaar/${listing.id}` : `/classified/${listing.id}`;
  return (
    <Link
      to={href}
      data-testid={`listing-card-${listing.id}`}
      className="block bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
    >
      <div className="aspect-video bg-gray-100 relative overflow-hidden">
        {img
          ? <img src={img} alt={listing.title} className="w-full h-full object-cover" />
          : <div className="flex items-center justify-center h-full text-gray-300"><Package className="w-10 h-10" /></div>
        }
        {/* No Sell/Buy chip: every Bazaar listing is a Sell, so the badge said nothing.
            Classifieds still carry one because it is what tells the two apart. */}
        {kind === 'classified' && (
          <span className="absolute top-2 left-2 text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
            Classified
          </span>
        )}
        {listing.sellerVerified && (
          <span
            className="absolute left-2 bg-blue-600 text-white text-xs font-semibold px-2 py-0.5 rounded-full flex items-center gap-1"
            style={{ top: kind === 'classified' ? '1.75rem' : '0.5rem' }}
          >
            <CheckCircle className="w-3 h-3" /> Verified
          </span>
        )}
        <ListingFavouriteButton listingId={listing.id} kind={kind} isFavourited={listing.isFavourited} canFavourite={canFavourite} onLoginRequired={onLoginRequired} />
      </div>
      <div className="p-3">
        <p className="text-sm font-semibold text-gray-900 truncate">{listing.title}</p>
        <p className="text-xs text-gray-500 mt-0.5 truncate">{listing.category} · {listing.location}</p>
        <div className="flex items-center justify-between mt-2">
          <span className="text-base font-bold text-blue-700">
            {kind === 'bazaar' && (listing as BazaarListing).pricingType === BazaarPricingType.Open ? 'Open to offers' : `£${listing.intentionPrice.toFixed(2)}`}
          </span>
          {quoteCount > 0 && (
            <span className="text-xs text-gray-400">{quoteCount} {quoteCount === 1 ? 'quote' : 'quotes'}</span>
          )}
        </div>
      </div>
    </Link>
  );
}

// Mirrors mobile's ListingFavouriteButton (mobile/src/components/bazaar/ListingCard.tsx).
function ListingFavouriteButton({ listingId, kind, isFavourited, canFavourite, onLoginRequired }: {
  listingId: string;
  kind: 'bazaar' | 'classified';
  isFavourited?: boolean;
  canFavourite: boolean;
  onLoginRequired: () => void;
}) {
  const { getToken } = useAppAuth();
  const qc = useQueryClient();
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: [`${kind}-favourites`] });
    qc.invalidateQueries({ queryKey: [kind, 'browse'] });
  };
  const svc = kind === 'bazaar'
    ? bazaarService(createApiClient(getToken))
    : classifiedService(createApiClient(getToken));

  const addMutation = useMutation({ mutationFn: () => svc.addFavourite(listingId), onSuccess: invalidate });
  const removeMutation = useMutation({ mutationFn: () => svc.removeFavourite(listingId), onSuccess: invalidate });
  const isPending = addMutation.isPending || removeMutation.isPending;

  return (
    <button
      data-testid={`listing-favourite-${listingId}-button`}
      onClick={e => {
        e.preventDefault();
        e.stopPropagation();
        if (!canFavourite) { onLoginRequired(); return; }
        if (isFavourited) removeMutation.mutate(); else addMutation.mutate();
      }}
      disabled={isPending}
      className="absolute top-2 right-2 bg-white/90 rounded-full p-1.5 hover:bg-white transition-colors disabled:opacity-60"
    >
      <Heart className={`w-4 h-4 ${isFavourited ? 'fill-red-500 text-red-500' : 'text-gray-500'}`} />
    </button>
  );
}

function RadiusPicker({ radiusSelection, onChange }: { radiusSelection: number | 'nationwide'; onChange: (r: number | 'nationwide') => void }) {
  return (
    <div className="flex flex-wrap gap-2">
      {RADIUS_OPTIONS.map(r => (
        <button
          key={r}
          type="button"
          data-testid={`filter-radius-${r}`}
          onClick={() => onChange(r)}
          className={`px-3 py-1.5 rounded-lg border text-xs font-semibold ${
            radiusSelection === r ? 'bg-blue-600 border-blue-600 text-white' : 'border-gray-300 text-gray-600 hover:bg-gray-50'
          }`}
        >
          {r === 'nationwide' ? 'Nationwide' : `${r} mi`}
        </button>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  1a. BAZAAR BROWSE PAGE
// ─────────────────────────────────────────────────────────────────────────────
export function BazaarBrowsePage() {
  const { account, login, getToken } = useAppAuth();
  const [search, setSearch]   = useState('');
  const [minPrice, setMin]    = useState('');
  const [maxPrice, setMax]    = useState('');
  const [category, setCat]    = useState('');
  const [showFilter, setShowFilter] = useState(false);
  const [debouncedSearch, setDebounced] = useState('');
  const [showFavouritesOnly, setShowFavouritesOnly] = useState(false);

  const [postcode, setPostcode]         = useState('');
  const [coords, setCoords]             = useState<{ lat: number; lng: number } | undefined>(undefined);
  const [radiusSelection, setRadiusSelection] = useState<number | 'nationwide'>(25);
  const [sortBy, setSortBy]             = useState<BazaarSearchParams['sortBy']>('nearest');
  const [geocodeError, setGeocodeError] = useState<string | null>(null);
  const [categoryId, setCategoryId]     = useState('');
  const [attributeFilters, setAttributeFilters] = useState<ListingAttributeFilter[]>([]);
  const [sortAttrKey, setSortAttrKey]   = useState<string | undefined>(undefined);
  const [page, setPage]                 = useState(1);
  const PAGE_SIZE = 24;

  const { data: categories } = useQuery({
    queryKey: ['bazaar', 'categories'],
    queryFn:  () => bazaarCategoryService(createApiClient(getToken)).getTree(),
    staleTime: 5 * 60_000,
  });

  // The effective taxonomy node: an explicitly chosen subcategory wins, otherwise the
  // top-level category matched by name. Facets exist only inside one.
  const browseParent    = categories?.find(c => !c.parentId && c.name === category);
  const browseCategoryId = categoryId || browseParent?.id || '';

  // Same query key and staleTime as the create form, so switching back and forth between two
  // categories costs one request each rather than one per click.
  const { data: browseAttributeDefs, isLoading: browseAttributesLoading } = useQuery({
    queryKey: ['listing-categories', browseCategoryId, 'bazaar'],
    queryFn:  () => bazaarCategoryService(createApiClient(getToken))
      .getAttributes(browseCategoryId, 'bazaar'),
    enabled: !!browseCategoryId,
    staleTime: 5 * 60_000,
  });
  const browseDefinitions = browseAttributeDefs ?? [];

  // Changing category keeps only the facets the new category also defines. Unlike the create
  // form this happens silently - a filter is cheap to re-apply, and a toast on every category
  // click would be noise.
  useEffect(() => {
    if (!browseCategoryId) { setAttributeFilters([]); return; }
    if (browseDefinitions.length === 0) return;
    setAttributeFilters(prev => {
      const next = carryOverFilters(prev, browseDefinitions);
      return next.length === prev.length ? prev : next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [browseCategoryId, browseAttributeDefs]);

  // A sort keyed to an attribute the new category does not define would silently fall back
  // to CreatedAt, so drop it the moment it stops being valid.
  useEffect(() => {
    if (!sortAttrKey) return;
    if (browseDefinitions.some(d => d.isSortable && d.attrKey === sortAttrKey)) return;
    setSortAttrKey(undefined);
    setSortBy(prev => (prev === 'attr_asc' || prev === 'attr_desc' ? 'nearest' : prev));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [browseCategoryId, browseAttributeDefs]);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    if (postcode.trim() || coords || !navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      pos => setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => { /* denied/unavailable — just skip distance filtering */ },
    );
  }, [postcode, coords]);

  useEffect(() => {
    const pc = postcode.trim();
    if (!pc) { setGeocodeError(null); return; }
    const t = setTimeout(async () => {
      try {
        setCoords(await geocodePostcode(pc));
        setGeocodeError(null);
      } catch {
        setGeocodeError('Location not found — please check and try again.');
      }
    }, 500);
    return () => clearTimeout(t);
  }, [postcode]);

  const useCurrentLocation = () => {
    setPostcode('');
    setGeocodeError(null);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        pos => setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => setCoords(undefined),
      );
    } else {
      setCoords(undefined);
    }
  };

  const params: BazaarSearchParams = {
    search:      debouncedSearch.trim() || undefined,
    minPrice:    minPrice ? Number(minPrice) : undefined,
    maxPrice:    maxPrice ? Number(maxPrice) : undefined,
    category:    category || undefined,
    // browseCategoryId, not categoryId: picking only a top-level category leaves the
    // subcategory state empty, and sending no categoryId would make the API silently
    // ignore the facets and sort below - the same node the facets were resolved from
    // has to be the one the search is scoped to.
    categoryId:  browseCategoryId || undefined,
    postcode:    postcode.trim() || undefined,
    lat:         coords?.lat,
    lng:         coords?.lng,
    radiusMiles: radiusSelection === 'nationwide' ? undefined : radiusSelection,
    sortBy,
    // The API ignores these unless categoryId is set, which is also why they are cleared
    // whenever the category changes.
    attributeFilters: encodeAttributeFilters(attributeFilters),
    sortAttrKey,
  };

  // Any filter change invalidates the current page — jump back to page 1 rather than
  // showing a stale page number against a different result set.
  useEffect(() => { setPage(1); }, [
    debouncedSearch, minPrice, maxPrice, category, categoryId,
    postcode, coords, radiusSelection, sortBy, attributeFilters, sortAttrKey,
  ]);

  const authSvc = account ? bazaarService(createApiClient(getToken)) : null;

  const { data: browseResult, isLoading: browseLoading } = useQuery({
    queryKey: ['bazaar', 'browse', params, page],
    // Authenticated users must browse via the authed client so the backend can compute
    // IsFavourited against their own favourites — the public client is always anonymous
    // server-side, so hearts would never show filled in for a logged-in user otherwise.
    queryFn:  () => authSvc
      ? authSvc.browsePaged({ ...params, page, pageSize: PAGE_SIZE })
      : publicBazaarService.browsePaged({ ...params, page, pageSize: PAGE_SIZE }),
    enabled: !showFavouritesOnly,
  });
  const browseListings = browseResult?.items ?? [];
  const totalPages = browseResult ? Math.max(1, Math.ceil(browseResult.totalCount / PAGE_SIZE)) : 1;

  const { data: favourites = [], isLoading: favouritesLoading } = useQuery({
    queryKey: ['bazaar-favourites'],
    queryFn:  () => authSvc!.getFavourites(),
    enabled: showFavouritesOnly && !!authSvc,
  });

  const listings   = showFavouritesOnly ? favourites.map(bazaarFavouriteToListing) : browseListings;
  const isLoading  = showFavouritesOnly ? favouritesLoading : browseLoading;
  const hasLocationFilter = !!postcode.trim();

  return (
    <div data-testid="bazaar-screen" className="page-shell-tight">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Bazaar</h1>
        </div>
        {account ? (
          <Link
            to="/bazaar/create?type=sell"
            data-testid="bazaar-create-button"
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" /> Sell
          </Link>
        ) : (
          <button
            data-testid="bazaar-create-button"
            onClick={() => login()}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" /> Sell
          </button>
        )}
      </div>

      <div className="flex gap-2 mb-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          <input
            data-testid="bazaar-search-input"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search listings…"
            className="w-full border border-gray-300 rounded-lg pl-9 pr-8 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <button
          data-testid="bazaar-filter-button"
          onClick={() => setShowFilter(v => !v)}
          className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
            showFilter ? 'bg-blue-50 border-blue-300 text-blue-700' : 'border-gray-300 text-gray-600 hover:bg-gray-50'
          }`}
        >
          <Filter className="w-4 h-4" /> Filters
        </button>
        {account && (
          <button
            data-testid="bazaar-favourites-filter-button"
            onClick={() => setShowFavouritesOnly(v => !v)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
              showFavouritesOnly ? 'bg-red-50 border-red-300 text-red-600' : 'border-gray-300 text-gray-600 hover:bg-gray-50'
            }`}
          >
            <Heart className={`w-4 h-4 ${showFavouritesOnly ? 'fill-red-500 text-red-500' : ''}`} /> Favourites
          </button>
        )}
        {account ? (
          <Link
            to="/bazaar/mine"
            data-testid="bazaar-my-listings-link"
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-600 hover:bg-gray-50"
          >
            My Listings
          </Link>
        ) : (
          <button
            data-testid="bazaar-my-listings-link"
            onClick={() => login()}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-600 hover:bg-gray-50"
          >
            My Listings
          </button>
        )}
      </div>

      {!showFavouritesOnly && (
        hasLocationFilter ? (
          <div className="flex items-center gap-1.5 mb-4 -mt-2">
            <MapPin className="w-3.5 h-3.5 text-blue-700" />
            <span className="text-xs text-blue-700 font-medium">{postcode} · {radiusSelection === 'nationwide' ? 'Nationwide' : `${radiusSelection} miles`}</span>
            <button data-testid="use-current-location" onClick={useCurrentLocation} className="text-xs text-gray-400 hover:text-gray-600 underline ml-1">
              Use my current location instead
            </button>
          </div>
        ) : coords ? (
          <div className="flex items-center gap-1.5 mb-4 -mt-2 text-xs text-gray-500">
            <Navigation className="w-3.5 h-3.5" /> Near you · {radiusSelection === 'nationwide' ? 'Nationwide' : `${radiusSelection} miles`}
          </div>
        ) : (
          <p className="text-xs text-gray-400 mb-4 -mt-2">Enter a postcode or town in Filters to see nearby listings</p>
        )
      )}

      {showFilter && (
        <div data-testid="bazaar-filter-panel" className="bg-white border border-gray-200 rounded-xl p-4 mb-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Category</label>
            <select
              data-testid="filter-category"
              value={category}
              onChange={e => setCat(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All categories</option>
              {topLevelNames(categories).map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          {(() => {
            const matchedParent = categories?.find(c => !c.parentId && c.name === category);
            const subcategories = matchedParent ? categories?.filter(c => c.parentId === matchedParent.id) : [];
            if (!subcategories?.length) return null;
            return (
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Subcategory</label>
                <select
                  data-testid="filter-subcategory"
                  value={categoryId}
                  onChange={e => setCategoryId(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All {category}</option>
                  {subcategories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            );
          })()}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Min price (£)</label>
            <input
              data-testid="filter-min-price"
              type="number"
              min="0"
              value={minPrice}
              onChange={e => setMin(e.target.value)}
              placeholder="0"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Max price (£)</label>
            <input
              data-testid="filter-max-price"
              type="number"
              min="0"
              value={maxPrice}
              onChange={e => setMax(e.target.value)}
              placeholder="No limit"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="sm:col-span-3">
            <label className="block text-xs font-semibold text-gray-600 mb-1">Postcode or town</label>
            <PostcodeInput value={postcode} onChange={setPostcode} placeholder="e.g. Leeds, LS1, SW1A 1AA" testId="filter-postcode" />
            {geocodeError && <p className="text-xs text-red-500 mt-1">{geocodeError}</p>}
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs font-semibold text-gray-600 mb-1">Search radius</label>
            <RadiusPicker radiusSelection={radiusSelection} onChange={setRadiusSelection} />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Sort by</label>
            <select
              data-testid="filter-sort"
              value={sortBy}
              onChange={e => {
                const [mode, attrKey] = e.target.value.split('|');
                setSortBy(mode as BazaarSearchParams['sortBy']);
                setSortAttrKey(attrKey || undefined);
              }}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {SORT_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              {/* The attribute sorts carry their key in the option value, so one select can
                  express both "how" and "on what" without a second control. */}
              {attributeSortOptions(browseDefinitions).map(opt => (
                <option
                  key={`${opt.attrKey}-${opt.sortBy}`}
                  data-testid={`sort-attr-${opt.attrKey}-${opt.sortBy === 'attr_asc' ? 'asc' : 'desc'}`}
                  value={`${opt.sortBy}|${opt.attrKey}`}
                >
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          {/* Category-specific facets. Only rendered inside a category, because the facets
              ARE that category's definitions - with none chosen there is nothing to offer
              beyond the price/distance filters above. */}
          {!!browseCategoryId && (
            <div className="sm:col-span-3 border-t border-gray-100 pt-4">
              <ListingAttributeFilters
                definitions={browseDefinitions}
                filters={attributeFilters}
                loading={browseAttributesLoading}
                onChange={setAttributeFilters}
              />
            </div>
          )}
          <div className="sm:col-span-3 flex justify-end">
            <button
              data-testid="filters-close"
              onClick={() => {
                setShowFilter(false);
                setMin(''); setMax(''); setCat(''); setCategoryId('');
                setAttributeFilters([]);
                setRadiusSelection(25); setSortBy('nearest');
                useCurrentLocation();
              }}
              className="text-sm text-gray-500 hover:text-gray-700 underline"
            >
              Clear & close
            </button>
          </div>
        </div>
      )}

      {isLoading ? (
        <div data-testid="bazaar-loading" className="flex justify-center py-16">
          <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full" />
        </div>
      ) : listings.length === 0 ? (
        <div data-testid="bazaar-empty" className="text-center py-16 text-gray-400">
          <Package className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="font-semibold">{showFavouritesOnly ? 'No favourites yet' : 'No listings found'}</p>
          <p className="text-sm mt-1">
            {showFavouritesOnly
              ? 'Tap the heart on a listing to save it here'
              : hasLocationFilter ? 'Try a wider radius or clear the location filter' : 'Try adjusting your search or filters'}
          </p>
        </div>
      ) : (
        <div data-testid="bazaar-listings-list" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {listings.map(l => (
            <ListingCard
              key={l.id}
              listing={l}
              kind="bazaar"
              canFavourite={!!account}
              onLoginRequired={login}
            />
          ))}
        </div>
      )}

      {!showFavouritesOnly && !isLoading && totalPages > 1 && (
        <div data-testid="bazaar-pagination" className="flex items-center justify-center gap-3 mt-6">
          <button
            data-testid="bazaar-page-prev"
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-40"
          >
            Previous
          </button>
          <span className="text-sm text-gray-500">Page {page} of {totalPages}</span>
          <button
            data-testid="bazaar-page-next"
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-40"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  1b. CLASSIFIEDS BROWSE PAGE
// ─────────────────────────────────────────────────────────────────────────────
export function ClassifiedsBrowsePage() {
  const { account, login, getToken } = useAppAuth();
  const [search, setSearch]   = useState('');
  const [minPrice, setMin]    = useState('');
  const [maxPrice, setMax]    = useState('');
  const [category, setCat]    = useState('');
  const [showFilter, setShowFilter] = useState(false);
  const [debouncedSearch, setDebounced] = useState('');
  const [showFavouritesOnly, setShowFavouritesOnly] = useState(false);

  const [postcode, setPostcode]         = useState('');
  const [coords, setCoords]             = useState<{ lat: number; lng: number } | undefined>(undefined);
  const [radiusSelection, setRadiusSelection] = useState<number | 'nationwide'>(25);
  const [sortBy, setSortBy]             = useState<ClassifiedSearchParams['sortBy']>('nearest');
  const [geocodeError, setGeocodeError] = useState<string | null>(null);
  const [categoryId, setCategoryId]     = useState('');
  const [attributeFilters, setAttributeFilters] = useState<ListingAttributeFilter[]>([]);
  const [sortAttrKey, setSortAttrKey]   = useState<string | undefined>(undefined);

  const { data: categories } = useQuery({
    queryKey: ['bazaar', 'categories'],
    queryFn:  () => bazaarCategoryService(createApiClient(getToken)).getTree(),
    staleTime: 5 * 60_000,
  });

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    if (postcode.trim() || coords || !navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      pos => setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => { /* denied/unavailable — just skip distance filtering */ },
    );
  }, [postcode, coords]);

  useEffect(() => {
    const pc = postcode.trim();
    if (!pc) { setGeocodeError(null); return; }
    const t = setTimeout(async () => {
      try {
        setCoords(await geocodePostcode(pc));
        setGeocodeError(null);
      } catch {
        setGeocodeError('Location not found — please check and try again.');
      }
    }, 500);
    return () => clearTimeout(t);
  }, [postcode]);

  const useCurrentLocation = () => {
    setPostcode('');
    setGeocodeError(null);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        pos => setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => setCoords(undefined),
      );
    } else {
      setCoords(undefined);
    }
  };

  // The effective taxonomy node. Classifieds gained this alongside Bazaar - ads posted
  // before the taxonomy existed simply have no CategoryId and are unaffected.
  const browseParent     = categories?.find(c => !c.parentId && c.name === category);
  const browseCategoryId = categoryId || browseParent?.id || '';

  const { data: browseAttributeDefs, isLoading: browseAttributesLoading } = useQuery({
    queryKey: ['listing-categories', browseCategoryId, 'classified'],
    queryFn:  () => bazaarCategoryService(createApiClient(getToken))
      .getAttributes(browseCategoryId, 'classified'),
    enabled: !!browseCategoryId,
    staleTime: 5 * 60_000,
  });
  const browseDefinitions = browseAttributeDefs ?? [];

  // Changing category keeps only the facets the new category also defines, silently - a
  // filter is cheap to re-apply, unlike the typed values on the create form.
  useEffect(() => {
    if (!browseCategoryId) { setAttributeFilters([]); return; }
    if (browseDefinitions.length === 0) return;
    setAttributeFilters(prev => {
      const next = carryOverFilters(prev, browseDefinitions);
      return next.length === prev.length ? prev : next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [browseCategoryId, browseAttributeDefs]);

  // A sort keyed to an attribute the new category does not define would silently fall back
  // to CreatedAt, so drop it the moment it stops being valid.
  useEffect(() => {
    if (!sortAttrKey) return;
    if (browseDefinitions.some(d => d.isSortable && d.attrKey === sortAttrKey)) return;
    setSortAttrKey(undefined);
    setSortBy(prev => (prev === 'attr_asc' || prev === 'attr_desc' ? 'nearest' : prev));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [browseCategoryId, browseAttributeDefs]);

  const params: ClassifiedSearchParams = {
    search:      debouncedSearch.trim() || undefined,
    minPrice:    minPrice ? Number(minPrice) : undefined,
    maxPrice:    maxPrice ? Number(maxPrice) : undefined,
    category:    category || undefined,
    categoryId:  browseCategoryId || undefined,
    postcode:    postcode.trim() || undefined,
    lat:         coords?.lat,
    lng:         coords?.lng,
    radiusMiles: radiusSelection === 'nationwide' ? undefined : radiusSelection,
    sortBy,
    attributeFilters: encodeAttributeFilters(attributeFilters),
    sortAttrKey,
  };

  const authSvc = account ? classifiedService(createApiClient(getToken)) : null;

  const { data: browseListings = [], isLoading: browseLoading } = useQuery({
    queryKey: ['classified', 'browse', params],
    // See BazaarBrowsePage — must use the authed client when logged in so the backend
    // computes IsFavourited correctly (the public client is always anonymous server-side).
    queryFn:  () => authSvc ? authSvc.browse(params) : publicClassifiedService.browse(params),
    enabled: !showFavouritesOnly,
  });

  const { data: favourites = [], isLoading: favouritesLoading } = useQuery({
    queryKey: ['classified-favourites'],
    queryFn:  () => authSvc!.getFavourites(),
    enabled: showFavouritesOnly && !!authSvc,
  });

  const listings   = showFavouritesOnly ? favourites.map(classifiedFavouriteToListing) : browseListings;
  const isLoading  = showFavouritesOnly ? favouritesLoading : browseLoading;
  const hasLocationFilter = !!postcode.trim();

  return (
    <div data-testid="classifieds-screen" className="page-shell-tight">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Classifieds</h1>
        </div>
        {account ? (
          <Link
            to="/bazaar/create?type=classified"
            data-testid="bazaar-create-button"
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" /> Post Ad
          </Link>
        ) : (
          <button
            data-testid="bazaar-create-button"
            onClick={() => login()}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" /> Post Ad
          </button>
        )}
      </div>

      <div className="flex gap-2 mb-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          <input
            data-testid="bazaar-search-input"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search Classifieds…"
            className="w-full border border-gray-300 rounded-lg pl-9 pr-8 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <button
          data-testid="bazaar-filter-button"
          onClick={() => setShowFilter(v => !v)}
          className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
            showFilter ? 'bg-blue-50 border-blue-300 text-blue-700' : 'border-gray-300 text-gray-600 hover:bg-gray-50'
          }`}
        >
          <Filter className="w-4 h-4" /> Filters
        </button>
        {account && (
          <button
            data-testid="bazaar-favourites-filter-button"
            onClick={() => setShowFavouritesOnly(v => !v)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
              showFavouritesOnly ? 'bg-red-50 border-red-300 text-red-600' : 'border-gray-300 text-gray-600 hover:bg-gray-50'
            }`}
          >
            <Heart className={`w-4 h-4 ${showFavouritesOnly ? 'fill-red-500 text-red-500' : ''}`} /> Favourites
          </button>
        )}
      </div>

      {!showFavouritesOnly && (
        hasLocationFilter ? (
          <div className="flex items-center gap-1.5 mb-4 -mt-2">
            <MapPin className="w-3.5 h-3.5 text-blue-700" />
            <span className="text-xs text-blue-700 font-medium">{postcode} · {radiusSelection === 'nationwide' ? 'Nationwide' : `${radiusSelection} miles`}</span>
            <button data-testid="use-current-location" onClick={useCurrentLocation} className="text-xs text-gray-400 hover:text-gray-600 underline ml-1">
              Use my current location instead
            </button>
          </div>
        ) : coords ? (
          <div className="flex items-center gap-1.5 mb-4 -mt-2 text-xs text-gray-500">
            <Navigation className="w-3.5 h-3.5" /> Near you · {radiusSelection === 'nationwide' ? 'Nationwide' : `${radiusSelection} miles`}
          </div>
        ) : (
          <p className="text-xs text-gray-400 mb-4 -mt-2">Enter a postcode or town in Filters to see nearby listings</p>
        )
      )}

      {showFilter && (
        <div data-testid="bazaar-filter-panel" className="bg-white border border-gray-200 rounded-xl p-4 mb-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Category</label>
            <select
              data-testid="filter-category"
              value={category}
              onChange={e => setCat(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All categories</option>
              {topLevelNames(categories).map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          {(() => {
            const subcategories = browseParent
              ? categories?.filter(c => c.parentId === browseParent.id)
              : [];
            if (!subcategories?.length) return null;
            return (
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Subcategory</label>
                <select
                  data-testid="filter-subcategory"
                  value={categoryId}
                  onChange={e => setCategoryId(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All {category}</option>
                  {subcategories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            );
          })()}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Min price (£)</label>
            <input
              data-testid="filter-min-price"
              type="number"
              min="0"
              value={minPrice}
              onChange={e => setMin(e.target.value)}
              placeholder="0"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Max price (£)</label>
            <input
              data-testid="filter-max-price"
              type="number"
              min="0"
              value={maxPrice}
              onChange={e => setMax(e.target.value)}
              placeholder="No limit"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="sm:col-span-3">
            <label className="block text-xs font-semibold text-gray-600 mb-1">Postcode or town</label>
            <PostcodeInput value={postcode} onChange={setPostcode} placeholder="e.g. Leeds, LS1, SW1A 1AA" testId="filter-postcode" />
            {geocodeError && <p className="text-xs text-red-500 mt-1">{geocodeError}</p>}
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs font-semibold text-gray-600 mb-1">Search radius</label>
            <RadiusPicker radiusSelection={radiusSelection} onChange={setRadiusSelection} />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Sort by</label>
            <select
              data-testid="filter-sort"
              value={sortBy}
              onChange={e => {
                const [mode, attrKey] = e.target.value.split('|');
                setSortBy(mode as ClassifiedSearchParams['sortBy']);
                setSortAttrKey(attrKey || undefined);
              }}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {SORT_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              {attributeSortOptions(browseDefinitions).map(opt => (
                <option
                  key={`${opt.attrKey}-${opt.sortBy}`}
                  data-testid={`sort-attr-${opt.attrKey}-${opt.sortBy === 'attr_asc' ? 'asc' : 'desc'}`}
                  value={`${opt.sortBy}|${opt.attrKey}`}
                >
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          {/* Category-specific facets — same component, same behaviour as Bazaar browse. */}
          {!!browseCategoryId && (
            <div className="sm:col-span-3 border-t border-gray-100 pt-4">
              <ListingAttributeFilters
                definitions={browseDefinitions}
                filters={attributeFilters}
                loading={browseAttributesLoading}
                onChange={setAttributeFilters}
              />
            </div>
          )}
          <div className="sm:col-span-3 flex justify-end">
            <button
              data-testid="filters-close"
              onClick={() => {
                setShowFilter(false);
                setMin(''); setMax(''); setCat(''); setCategoryId('');
                setAttributeFilters([]);
                setRadiusSelection(25); setSortBy('nearest');
                useCurrentLocation();
              }}
              className="text-sm text-gray-500 hover:text-gray-700 underline"
            >
              Clear & close
            </button>
          </div>
        </div>
      )}

      {isLoading ? (
        <div data-testid="bazaar-loading" className="flex justify-center py-16">
          <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full" />
        </div>
      ) : listings.length === 0 ? (
        <div data-testid="bazaar-empty" className="text-center py-16 text-gray-400">
          <Package className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="font-semibold">{showFavouritesOnly ? 'No favourites yet' : 'No ads found'}</p>
          <p className="text-sm mt-1">
            {showFavouritesOnly
              ? 'Tap the heart on an ad to save it here'
              : hasLocationFilter ? 'Try a wider radius or clear the location filter' : 'Try adjusting your search or filters'}
          </p>
        </div>
      ) : (
        <div data-testid="bazaar-listings-list" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {listings.map(l => (
            <ListingCard
              key={l.id}
              listing={l}
              kind="classified"
              canFavourite={!!account}
              onLoginRequired={login}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  1c. BAZAAR SELLER SHOP PAGE — "View shop" from a listing's seller card
// ─────────────────────────────────────────────────────────────────────────────
export function BazaarSellerShopPage() {
  const { sellerId } = useParams<{ sellerId: string }>();
  const { account, login, getToken } = useAppAuth();
  const authSvc = account ? bazaarService(createApiClient(getToken)) : null;

  const { data: listings = [], isLoading } = useQuery({
    queryKey: ['bazaar', 'seller-shop', sellerId],
    queryFn:  () => (authSvc ?? publicBazaarService).getBySeller(sellerId!),
    enabled:  !!sellerId,
  });

  const sellerName = listings[0]?.sellerName ?? 'Seller';

  return (
    <div className="page-shell-tight">
      <Link to="/bazaar" className="flex items-center gap-1 text-sm text-blue-600 hover:underline mb-4">
        <ArrowLeft className="w-4 h-4" /> Back to Bazaar
      </Link>
      <h1 data-testid="seller-shop-title" className="text-2xl font-bold text-gray-900 mb-1 flex items-center gap-2">
        <Store className="w-6 h-6 text-gray-400" /> {sellerName}&apos;s Shop
      </h1>
      <p className="text-sm text-gray-500 mb-6">{listings.length} active listing{listings.length === 1 ? '' : 's'}</p>

      {isLoading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full" />
        </div>
      ) : listings.length === 0 ? (
        <div className="text-center py-16 text-gray-400">This seller has no active listings right now.</div>
      ) : (
        <div data-testid="seller-shop-listings" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {listings.map(l => (
            <ListingCard key={l.id} listing={l} kind="bazaar" canFavourite={!!account} onLoginRequired={login} />
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  2a. BAZAAR LISTING DETAIL PAGE
// ─────────────────────────────────────────────────────────────────────────────
export function BazaarListingDetailPage() {
  const { id }        = useParams<{ id: string }>();
  const { getToken, account, login } = useAppAuth();
  const qc            = useQueryClient();
  const navigate      = useNavigate();
  const addToBasket   = useBazaarCartStore(s => s.addItem);
  const [offerPrice, setOffer]   = useState('');
  const [offerMsg, setMsg]       = useState('');
  const [activeImg, setActiveImg] = useState(0);
  const [submitError, setError]  = useState<string | null>(null);
  const [submitOk, setOk]        = useState(false);
  const [showChat, setShowChat]  = useState(false);

  const authSvc = account ? bazaarService(createApiClient(getToken)) : null;

  const { data: listing, isLoading } = useQuery({
    queryKey: ['bazaar', id],
    queryFn:  () => publicBazaarService.get(id!),
    enabled:  !!id,
  });

  const { data: quotes = [] } = useQuery({
    queryKey: ['bazaar', id, 'quotes'],
    queryFn:  () => authSvc!.getQuotes(id!),
    enabled:  !!id && !!listing && !!authSvc,
  });

  const { data: myProfile } = useQuery({
    queryKey: ['profile', 'me-for-delivery-link'],
    queryFn:  () => profileService(createApiClient(getToken)).getProfile(),
    enabled:  !!account,
  });

  const { data: categories } = useQuery({
    queryKey: ['bazaar', 'categories'],
    queryFn:  () => bazaarCategoryService(createApiClient(getToken)).getTree(),
    staleTime: 5 * 60_000,
  });

  const { data: sellerReviews } = useQuery({
    queryKey: ['bazaar-reviews', 'seller', listing?.sellerId],
    queryFn:  () => bazaarReviewService(createApiClient(getToken)).getForSeller(listing!.sellerId),
    enabled:  !!listing?.sellerId,
  });

  const { data: matchingItems } = useQuery({
    queryKey: ['bazaar', 'matching', id, listing?.categoryId],
    queryFn:  () => (authSvc ?? publicBazaarService).browse({
      categoryId: listing!.categoryId, excludeId: id, lat: listing!.lat, lng: listing!.lng, radiusMiles: 40,
    }),
    enabled:  !!listing?.categoryId,
  });

  const { data: relatedItems } = useQuery({
    queryKey: ['bazaar', 'seller-items', listing?.sellerId, id],
    queryFn:  () => (authSvc ?? publicBazaarService).getBySeller(listing!.sellerId),
    enabled:  !!listing?.sellerId,
    select:   (data) => data.filter(l => l.id !== id),
  });

  // "Delivery from £X" for a collection-only listing. Sits with the other hooks rather than
  // beside the markup that uses it, because the early returns below would otherwise make the
  // call conditional.
  const { estimate: deliveryEstimate, label: deliveryEstimateLabel } = useDeliveryEstimate({
    listingId:    id,
    fromPostcode: listing?.postcode ?? listing?.location,
    toPostcode:   myProfile?.postcode,
    // Pre-purchase the buyer only ever sees the seller's outcode, so the figure must not be
    // presented as though it were exact.
    isIndicative: true,
    enabled:      listing?.deliveryOption === DeliveryOption.BuyerArranges,
  });

  const quoteMutation = useMutation({
    mutationFn: () => authSvc!.submitQuote(id!, {
      offerPrice: Number(offerPrice),
      message:    offerMsg.trim() || undefined,
    }),
    onSuccess: () => {
      setOk(true);
      setOffer('');
      setMsg('');
      qc.invalidateQueries({ queryKey: ['bazaar', id, 'quotes'] });
    },
    onError: (e: any) => setError(e?.message ?? 'Failed to submit quote'),
  });

  const acceptMutation = useMutation({
    mutationFn: (quoteId: string) => authSvc!.acceptQuote(id!, quoteId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bazaar', id] }),
  });

  const rejectMutation = useMutation({
    mutationFn: (quoteId: string) => authSvc!.rejectQuote(id!, quoteId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bazaar', id, 'quotes'] }),
  });

  const soldMutation = useMutation({
    mutationFn: () => authSvc!.markSold(id!),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['bazaar', id] }); navigate('/bazaar/mine'); },
  });

  const [buyNowError, setBuyNowError] = useState<string | null>(null);
  const buyNowMutation = useMutation({
    mutationFn: () => bazaarOrdersService(createApiClient(getToken)).create({ items: [{ listingId: id!, quantity: 1 }] }),
    onSuccess: (result) => {
      navigate(`/bazaar/checkout/${result.orderId}`, {
        state: { clientSecret: result.clientSecret, depositAmount: result.depositAmount },
      });
    },
    onError: (e: any) => setBuyNowError(e?.message ?? 'Could not start checkout. Please try again.'),
  });

  const favouriteMutation = useMutation({
    mutationFn: () => (listing?.isFavourited ? authSvc!.removeFavourite(id!) : authSvc!.addFavourite(id!)),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bazaar', id] }),
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-20">
        <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!listing) {
    return <div className="text-center py-20 text-gray-500">Listing not found.</div>;
  }

  // account.userId is the identity provider's own user id, not this app's UserId - comparing it
  // to listing.sellerId (the app's UserId) never matches for a real Entra-authenticated user,
  // so the seller never saw their own "Mark as Sold" controls. myProfile (fetched above) carries
  // the actual UserId.
  const isSeller = !!myProfile && listing.sellerId === myProfile.userId;
  const isOpen   = listing.pricingType === BazaarPricingType.Open;

  return (
    <div data-testid="listing-detail-screen" className="page-shell-tight">
      <Link to="/bazaar" className="flex items-center gap-1 text-sm text-blue-600 hover:underline mb-4">
        <ArrowLeft className="w-4 h-4" /> Back to Bazaar
      </Link>

      {(() => {
        const category = categories?.find(c => c.id === listing.categoryId);
        const parentCategory = category?.parentId ? categories?.find(c => c.id === category.parentId) : undefined;
        if (!category) return null;
        return (
          <nav data-testid="listing-breadcrumb" className="flex items-center gap-1.5 text-xs text-gray-500 mb-3">
            <Link to="/bazaar" className="hover:text-blue-600 hover:underline">Bazaar</Link>
            {parentCategory && (
              <>
                <span>›</span>
                <Link to={`/bazaar?category=${parentCategory.slug}`} className="hover:text-blue-600 hover:underline">{parentCategory.name}</Link>
              </>
            )}
            <span>›</span>
            <span className="text-gray-700 font-medium">{category.name}</span>
          </nav>
        );
      })()}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <div className="relative aspect-video bg-gray-100 rounded-xl overflow-hidden mb-2">
            {listing.media.length > 0
              ? <img src={listing.media[activeImg]?.url} alt={listing.title} className="w-full h-full object-cover" />
              : <div className="flex items-center justify-center h-full text-gray-300"><Package className="w-12 h-12" /></div>
            }
            {listing.media.length > 0 && (
              <span className="absolute top-2 left-2 text-[10px] font-bold uppercase tracking-wide bg-black/60 text-white px-2 py-0.5 rounded-full">
                {listing.media.length} photo{listing.media.length === 1 ? '' : 's'}
              </span>
            )}
          </div>
          {listing.media.length > 1 && (
            <div className="flex gap-2 overflow-x-auto">
              {listing.media.map((m, i) => (
                <button
                  key={i}
                  onClick={() => setActiveImg(i)}
                  className={`w-14 h-14 rounded-lg overflow-hidden border-2 shrink-0 ${i === activeImg ? 'border-blue-500' : 'border-transparent'}`}
                >
                  <img src={m.url} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <div className="flex items-start justify-between gap-2 mb-2">
            <h1 data-testid="listing-detail-title" className="text-xl font-bold text-gray-900">{listing.title}</h1>
            <span className={`shrink-0 text-xs font-semibold px-2 py-0.5 rounded-full ${STATUS_COLORS[listing.status]}`}>
              {STATUS_LABELS[listing.status]}
            </span>
          </div>

          <div className="flex items-center gap-2 mb-3">
            <span className="text-xs text-gray-500">{listing.category}</span>
            {listing.condition && <span className="text-xs text-gray-500">· {listing.condition}</span>}
          </div>

          <p className="text-2xl font-bold text-blue-700 mb-1">
            {isOpen ? 'Open to offers' : `£${listing.intentionPrice.toFixed(2)}`}
          </p>
          <p className="text-xs text-gray-400 mb-2">
            Ref #TD-BZ-{listing.id.slice(0, 8).toUpperCase()}
          </p>

          {!!listing.favouritedCount && listing.favouritedCount > 0 && (
            <div className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 bg-amber-50 rounded-full px-2.5 py-1 mb-3">
              <Heart className="w-3 h-3" /> {listing.favouritedCount} favourited
            </div>
          )}

          <div className="bg-gray-50 rounded-lg p-3 mb-3 space-y-1.5">
            <div className="flex items-center gap-2 text-sm text-gray-700">
              <Star className="w-4 h-4 text-blue-500" />
              <span className="font-medium">{listing.sellerName}</span>
              {listing.sellerVerified && (
                <span className="flex items-center gap-0.5 text-xs text-blue-600 font-semibold">
                  <CheckCircle className="w-3 h-3" /> Verified
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <MapPin className="w-4 h-4 text-gray-400" />
              {listing.location}
              {listing.distanceMiles != null && <span className="text-gray-400">· {listing.distanceMiles.toFixed(1)} mi</span>}
            </div>
          </div>

          <p className="text-sm text-gray-700 mb-4 whitespace-pre-line">{listing.description}</p>

          {listing.contactPhone && (
            <p className="text-sm text-gray-600 mb-1"><span className="font-medium">Phone:</span> {listing.contactPhone}</p>
          )}
          {listing.contactEmail && (
            <p className="text-sm text-gray-600 mb-4"><span className="font-medium">Email:</span> {listing.contactEmail}</p>
          )}

          {isSeller && listing.status === ListingStatus.Active && (
            <button
              data-testid="listing-mark-sold-button"
              onClick={() => soldMutation.mutate()}
              disabled={soldMutation.isPending}
              className="w-full mb-3 py-2 rounded-lg border border-gray-300 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              {soldMutation.isPending ? 'Updating…' : 'Mark as Sold'}
            </button>
          )}

          {!account && listing.status === ListingStatus.Active && (
            <div data-testid="sign-in-to-respond" className="border border-blue-200 bg-blue-50 rounded-xl p-4">
              <p className="text-sm font-semibold text-blue-900 mb-1">Want to respond to this listing?</p>
              <p className="text-xs text-blue-700 mb-3">Sign in to make an offer, submit a quote, or contact the seller.</p>
              <button
                data-testid="sign-in-to-respond-button"
                onClick={() => login()}
                className="w-full py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700"
              >
                Sign In to Respond
              </button>
            </div>
          )}

          {!!account && !isSeller && listing.listingType === BazaarListingType.Sell && listing.status === ListingStatus.Active && (
            <div className="mb-3">
              <button
                data-testid="listing-buy-now-button"
                onClick={() => buyNowMutation.mutate()}
                disabled={buyNowMutation.isPending}
                className="w-full py-3 bg-green-600 text-white rounded-xl text-sm font-bold hover:bg-green-700 disabled:opacity-50 transition-colors"
              >
                {buyNowMutation.isPending ? 'Starting checkout…' : `Buy Now — £${listing.intentionPrice.toFixed(2)}`}
              </button>
              {buyNowError && <p className="text-xs text-red-600 mt-1">{buyNowError}</p>}
              <button
                data-testid="listing-add-to-basket-button"
                onClick={() => {
                  addToBasket({
                    listingId: listing.id,
                    listingTitle: listing.title,
                    sellerId: listing.sellerId,
                    sellerName: listing.sellerName ?? 'Unknown',
                    quantity: 1,
                    unitPrice: listing.intentionPrice,
                  });
                  navigate('/bazaar/basket');
                }}
                className="w-full mt-2 py-2 rounded-lg border border-green-300 text-sm font-semibold text-green-700 hover:bg-green-50"
              >
                Add to Basket
              </button>
              {isOpen && (
                <button
                  data-testid="listing-make-offer-button"
                  onClick={() => document.getElementById('quote-form-section')?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
                  className="w-full mt-2 py-2 rounded-lg border border-gray-300 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                >
                  Make an offer
                </button>
              )}
              <button
                data-testid="listing-add-to-favourites-button"
                onClick={() => favouriteMutation.mutate()}
                disabled={favouriteMutation.isPending}
                className="w-full mt-2 py-2 rounded-lg border border-gray-300 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50 flex items-center justify-center gap-1.5"
              >
                <Heart className={`w-4 h-4 ${listing.isFavourited ? 'fill-red-500 text-red-500' : ''}`} />
                {listing.isFavourited ? 'Remove from favourites' : 'Add to favourites'}
              </button>
            </div>
          )}

          {!!account && !isSeller && listing.status === ListingStatus.Active && (
            <button
              data-testid="listing-chat-with-seller-button"
              onClick={() => setShowChat(true)}
              className="w-full mb-3 py-2 rounded-lg border border-gray-300 text-sm font-semibold text-gray-700 hover:bg-gray-50"
            >
              Chat with Seller
            </button>
          )}

          {listing.status === ListingStatus.Active && (
            <div className="border border-gray-200 rounded-xl p-4 mb-3">
              <h3 className="text-sm font-bold text-gray-900 mb-2">Logistics &amp; Returns</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between items-center">
                  <span className="text-gray-500">Delivery method</span>
                  {listing.deliveryOption === DeliveryOption.NotApplicable ? (
                    <span className="text-gray-700">Not applicable</span>
                  ) : (
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                      listing.deliveryOption === DeliveryOption.BuyerArranges
                        ? 'bg-amber-100 text-amber-700'
                        : 'bg-blue-100 text-blue-700'
                    }`}>
                      {listing.deliveryOption === DeliveryOption.IncludesDelivery ? 'Delivery included'
                        : listing.deliveryOption === DeliveryOption.PlatformDelivery ? 'Platform delivery available'
                        : 'Collection only'}
                      {listing.deliveryPrice != null && listing.deliveryPrice > 0 && ` · £${listing.deliveryPrice.toFixed(2)}`}
                    </span>
                  )}
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Collection point</span>
                  <span className="text-gray-700">{isSeller ? listing.postcode : listing.postcode?.split(' ')[0]}{listing.postcode ? ', ' : ''}{listing.location}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Returns</span>
                  <span className="text-gray-700">Not accepted — inspect on collection</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Payment</span>
                  <span className="text-gray-700">Card, bank transfer</span>
                </div>
              </div>

              {isSeller && listing.deliveryOption === DeliveryOption.BuyerArranges && (
                <Link
                  data-testid="listing-post-delivery-job-link"
                  to={`/post-job?${new URLSearchParams({
                    pickupAddress: myProfile?.postcode ?? '',
                    description: `Delivery of: ${listing.title}. ${listing.description.slice(0, 100)}`,
                  })}`}
                  className="block w-full mt-3 py-2 text-center rounded-lg border border-blue-200 text-sm font-semibold text-blue-600 hover:bg-blue-50"
                >
                  Post a Delivery Job
                </Link>
              )}

              {!isSeller && !!account && listing.deliveryOption === DeliveryOption.BuyerArranges && (
                <div className="mt-3 border border-dashed border-green-300 bg-green-50 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <Boxes className="w-4 h-4 text-green-700" />
                    <span className="text-sm font-bold text-green-900">Can&apos;t collect it yourself?</span>
                    <span className="ml-auto text-[10px] font-bold tracking-wide uppercase bg-green-600 text-white px-2 py-0.5 rounded-full">Recommended</span>
                  </div>
                  <p className="text-xs text-green-800 mb-2">
                    This seller offers collection only. Post a delivery job and TransitDeal carriers will quote to collect and deliver it to you — tracked door to door.
                  </p>
                  {/* Indicative because the seller's location is only known to outcode
                      precision before purchase. Absent entirely when too few carriers cover
                      the route for a "from" figure to mean anything. */}
                  {deliveryEstimateLabel && (
                    <p className="text-sm font-bold text-green-900 mb-2" data-testid="listing-delivery-estimate">
                      {deliveryEstimateLabel}
                      <span className="block text-[11px] font-normal text-green-700">
                        Based on {deliveryEstimate?.providerCount} carriers covering this route.
                        Exact price once you post the job.
                      </span>
                    </p>
                  )}
                  <Link
                    data-testid="listing-get-delivered-link"
                    to={`/post-job?${new URLSearchParams({
                      pickupAddress: listing.postcode ?? listing.location,
                      deliveryAddress: myProfile?.postcode ?? '',
                      description: `Delivery of: ${listing.title}. ${listing.description.slice(0, 100)}`,
                    })}`}
                    className="block w-full py-2 text-center rounded-lg bg-green-600 text-white text-sm font-bold hover:bg-green-700"
                  >
                    Post a delivery job
                  </Link>
                </div>
              )}
            </div>
          )}

          {!isSeller && (
            <div className="border border-gray-200 rounded-xl p-4 mb-3">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-9 h-9 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold shrink-0">
                  {listing.sellerName?.slice(0, 2).toUpperCase()}
                </div>
                <div>
                  <div className="text-sm font-semibold text-gray-900 flex items-center gap-1.5">
                    {listing.sellerName}
                    {listing.sellerVerified && (
                      <span className="flex items-center gap-0.5 text-xs text-blue-600 font-semibold">
                        <CheckCircle className="w-3 h-3" /> Verified
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {sellerReviews && sellerReviews.count > 0 && (
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-bold text-gray-700">{sellerReviews.positivePercent}%</span>
                  <div className="flex-1 h-1.5 rounded-full bg-gray-200 overflow-hidden">
                    <div className="h-full bg-blue-600" style={{ width: `${sellerReviews.positivePercent ?? 0}%` }} />
                  </div>
                  <span className="text-xs text-gray-500 whitespace-nowrap">positive ({sellerReviews.count})</span>
                </div>
              )}

              {sellerReviews && (
                <div className="text-xs text-gray-500 mb-3">Items sold: <span className="font-semibold text-gray-700">{sellerReviews.itemsSold}</span></div>
              )}

              <div className="flex gap-2">
                {!!account && (
                  <button
                    data-testid="listing-message-seller-button"
                    onClick={() => setShowChat(true)}
                    className="flex-1 py-2 rounded-lg border border-gray-300 text-xs font-semibold text-gray-700 hover:bg-gray-50 flex items-center justify-center gap-1"
                  >
                    <MessageCircle className="w-3.5 h-3.5" /> Message
                  </button>
                )}
                <Link
                  data-testid="listing-view-shop-button"
                  to={`/bazaar/seller/${listing.sellerId}`}
                  className="flex-1 py-2 rounded-lg border border-gray-300 text-xs font-semibold text-gray-700 hover:bg-gray-50 flex items-center justify-center gap-1"
                >
                  <Store className="w-3.5 h-3.5" /> View shop
                </Link>
              </div>
            </div>
          )}

          {showChat && !!account && listing && (
            <div className="mb-3">
              <BazaarChatPanel
                listingId={id!}
                otherPartyId={listing.sellerId}
                otherPartyName={listing.sellerName}
                currentUserName={(account as any).name}
                getToken={getToken}
                onClose={() => setShowChat(false)}
              />
            </div>
          )}

          {!!account && !isSeller && isOpen && listing.status === ListingStatus.Active && (
            <div id="quote-form-section" data-testid="quote-form" className="border border-gray-200 rounded-xl p-4">
              <h3 className="text-sm font-bold text-gray-800 mb-3">Make an offer</h3>
              {submitOk ? (
                <div data-testid="quote-submitted" className="flex items-center gap-2 text-green-700 bg-green-50 rounded-lg p-3 text-sm font-semibold">
                  <CheckCircle className="w-4 h-4" /> Quote submitted successfully!
                </div>
              ) : (
                <>
                  {submitError && (
                    <div className="text-red-600 text-xs bg-red-50 rounded-lg px-3 py-2 mb-3">{submitError}</div>
                  )}
                  <div className="mb-3">
                    <label className="block text-xs font-medium text-gray-600 mb-1">Your offer price (£) *</label>
                    <input
                      data-testid="quote-offer-price-input"
                      type="number"
                      min="0"
                      step="0.01"
                      value={offerPrice}
                      onChange={e => { setOffer(e.target.value); setError(null); }}
                      placeholder="0.00"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div className="mb-3">
                    <label className="block text-xs font-medium text-gray-600 mb-1">Message (optional)</label>
                    <textarea
                      data-testid="quote-message-input"
                      value={offerMsg}
                      onChange={e => setMsg(e.target.value)}
                      rows={2}
                      placeholder="Any questions or notes for the seller…"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <button
                    data-testid="quote-submit-button"
                    onClick={() => {
                      if (!offerPrice || Number(offerPrice) <= 0) { setError('Please enter a valid offer price.'); return; }
                      quoteMutation.mutate();
                    }}
                    disabled={quoteMutation.isPending}
                    className="w-full py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-50"
                  >
                    {quoteMutation.isPending ? 'Submitting…' : 'Submit Quote'}
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {!!listing.attributes?.length && (
        <div className="mt-8">
          <h2 className="text-base font-bold text-gray-900 mb-3">Specification</h2>
          <div data-testid="listing-specification" className="bg-white border border-gray-200 rounded-xl overflow-hidden grid grid-cols-1 sm:grid-cols-2">
            {listing.attributes.map((a, i) => (
              <div
                key={a.key}
                className={`flex justify-between gap-4 px-4 py-2.5 text-sm ${i % 4 >= 2 ? 'bg-gray-50' : ''} border-b border-gray-100 sm:border-r sm:[&:nth-child(2n)]:border-r-0`}
              >
                <span className="text-gray-500">{a.key}</span>
                <span className="text-gray-900 font-medium text-right">{a.value}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {sellerReviews && sellerReviews.count > 0 && (
        <div className="mt-8">
          <h2 className="text-base font-bold text-gray-900 mb-1">Seller Reviews</h2>
          <p className="text-xs text-gray-500 mb-3">
            {sellerReviews.positivePercent}% positive · {sellerReviews.count} review{sellerReviews.count === 1 ? '' : 's'}
            {sellerReviews.averageRating != null && ` · ${sellerReviews.averageRating.toFixed(1)} avg rating`}
          </p>
          <div data-testid="listing-seller-reviews" className="space-y-3">
            {sellerReviews.reviews.map(r => (
              <div key={r.reviewId} className="bg-white border border-gray-200 rounded-xl p-3 text-sm">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-semibold text-gray-900">{r.reviewerName}</span>
                  <span className="text-amber-500 text-xs">{'★'.repeat(r.rating)}{'☆'.repeat(5 - r.rating)}</span>
                </div>
                {r.comment && <p className="text-gray-600 text-xs mb-1">{r.comment}</p>}
                <p className="text-gray-400 text-xs">{r.listingTitle} · {new Date(r.createdAt).toLocaleDateString()}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {!!matchingItems?.length && (
        <div className="mt-8">
          <h2 className="text-base font-bold text-gray-900 mb-1">Matching Items</h2>
          <p className="text-xs text-gray-500 mb-3">Same category, nearby</p>
          <div data-testid="listing-matching-items" className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {matchingItems.slice(0, 4).map(l => (
              <ListingCard key={l.id} listing={l} kind="bazaar" canFavourite={!!account} onLoginRequired={login} />
            ))}
          </div>
        </div>
      )}

      {!!relatedItems?.length && (
        <div className="mt-8">
          <h2 className="text-base font-bold text-gray-900 mb-1">Related Items</h2>
          <p className="text-xs text-gray-500 mb-3">More from {listing.sellerName}</p>
          <div data-testid="listing-related-items" className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {relatedItems.slice(0, 4).map(l => (
              <ListingCard key={l.id} listing={l} kind="bazaar" canFavourite={!!account} onLoginRequired={login} />
            ))}
          </div>
        </div>
      )}

      {isSeller && quotes.length > 0 && (
        <div className="mt-8">
          <h2 data-testid="quotes-section" className="text-base font-bold text-gray-900 mb-3">
            Quotes received ({quotes.length})
          </h2>
          <div className="space-y-3">
            {quotes.map(q => (
              <div key={q.id} data-testid={`quote-item-${q.id}`} className="bg-white border border-gray-200 rounded-xl p-4 flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm font-semibold text-gray-900">
                    {q.quoterName}
                    {q.quoterVerified && <span className="ml-2 text-xs text-blue-600">✓ Verified</span>}
                  </p>
                  <p className="text-lg font-bold text-blue-700">£{q.offerPrice.toFixed(2)}</p>
                  {q.message && <p className="text-xs text-gray-500 mt-0.5">{q.message}</p>}
                </div>
                <div className="flex gap-2">
                  {q.status === 0 /* Pending */ && (
                    <>
                      <button
                        data-testid={`quote-accept-${q.id}`}
                        onClick={() => acceptMutation.mutate(q.id)}
                        disabled={acceptMutation.isPending}
                        className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-xs font-semibold hover:bg-green-700 disabled:opacity-50"
                      >
                        Accept
                      </button>
                      <button
                        data-testid={`quote-reject-${q.id}`}
                        onClick={() => rejectMutation.mutate(q.id)}
                        disabled={rejectMutation.isPending}
                        className="px-3 py-1.5 border border-gray-300 text-gray-600 rounded-lg text-xs font-semibold hover:bg-gray-50 disabled:opacity-50"
                      >
                        Decline
                      </button>
                    </>
                  )}
                  {q.status !== 0 && (
                    <span className="text-xs text-gray-500 font-medium capitalize">
                      {['Pending','Accepted','Declined','Withdrawn','Expired'][q.status]}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  2b. CLASSIFIED LISTING DETAIL PAGE — contact-only, no quotes/cart/orders
// ─────────────────────────────────────────────────────────────────────────────
export function ClassifiedListingDetailPage() {
  const { id }        = useParams<{ id: string }>();
  const { getToken, account, login } = useAppAuth();
  const qc            = useQueryClient();
  const [activeImg, setActiveImg] = useState(0);

  const authSvc = account ? classifiedService(createApiClient(getToken)) : null;

  const { data: listing, isLoading } = useQuery({
    queryKey: ['classified', id],
    queryFn:  () => publicClassifiedService.get(id!),
    enabled:  !!id,
  });

  const { data: myProfile } = useQuery({
    queryKey: ['profile', 'me-for-classified-ownership'],
    queryFn:  () => profileService(createApiClient(getToken)).getProfile(),
    enabled:  !!account,
  });

  const { data: sellerReviews } = useQuery({
    queryKey: ['bazaar-reviews', 'seller', listing?.sellerId],
    queryFn:  () => bazaarReviewService(createApiClient(getToken)).getForSeller(listing!.sellerId),
    enabled:  !!listing?.sellerId,
  });

  const soldMutation = useMutation({
    mutationFn: () => authSvc!.markSold(id!),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['classified', id] }),
  });

  const cancelMutation = useMutation({
    mutationFn: () => authSvc!.cancel(id!),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['classified', id] }),
  });

  const favouriteMutation = useMutation({
    mutationFn: () => authSvc!.addFavourite(id!),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['classified-favourites'] }),
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-20">
        <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!listing) {
    return <div className="text-center py-20 text-gray-500">Ad not found.</div>;
  }

  // account.userId is the identity provider's own user id, not this app's UserId - see the same
  // fix in BazaarListingDetailPage above for why myProfile.userId is the correct comparison.
  const isSeller = !!myProfile && listing.sellerId === myProfile.userId;

  return (
    <div data-testid="listing-detail-screen" className="page-shell-tight">
      <Link to="/classifieds" className="flex items-center gap-1 text-sm text-blue-600 hover:underline mb-4">
        <ArrowLeft className="w-4 h-4" /> Back to Classifieds
      </Link>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <div className="aspect-video bg-gray-100 rounded-xl overflow-hidden mb-2">
            {listing.media.length > 0
              ? <img src={listing.media[activeImg]?.url} alt={listing.title} className="w-full h-full object-cover" />
              : <div className="flex items-center justify-center h-full text-gray-300"><Package className="w-12 h-12" /></div>
            }
          </div>
          {listing.media.length > 1 && (
            <div className="flex gap-2 overflow-x-auto">
              {listing.media.map((m, i) => (
                <button
                  key={i}
                  onClick={() => setActiveImg(i)}
                  className={`w-14 h-14 rounded-lg overflow-hidden border-2 shrink-0 ${i === activeImg ? 'border-blue-500' : 'border-transparent'}`}
                >
                  <img src={m.url} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <div className="flex items-start justify-between gap-2 mb-2">
            <h1 data-testid="listing-detail-title" className="text-xl font-bold text-gray-900">{listing.title}</h1>
            <span className={`shrink-0 text-xs font-semibold px-2 py-0.5 rounded-full ${STATUS_COLORS[listing.status]}`}>
              {STATUS_LABELS[listing.status]}
            </span>
          </div>

          <div className="flex items-center gap-2 mb-3">
            <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">Classified</span>
            <span className="text-xs text-gray-500">{listing.category}</span>
            {listing.condition && <span className="text-xs text-gray-500">· {listing.condition}</span>}
          </div>

          <p className="text-2xl font-bold text-blue-700 mb-3">£{listing.intentionPrice.toFixed(2)}</p>

          <div className="bg-gray-50 rounded-lg p-3 mb-3 space-y-1.5">
            <div className="flex items-center gap-2 text-sm text-gray-700">
              <Star className="w-4 h-4 text-blue-500" />
              <span className="font-medium">{listing.sellerName}</span>
              {listing.sellerVerified && (
                <span className="flex items-center gap-0.5 text-xs text-blue-600 font-semibold">
                  <CheckCircle className="w-3 h-3" /> Verified
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <MapPin className="w-4 h-4 text-gray-400" />
              {listing.location}
              {listing.distanceMiles != null && <span className="text-gray-400">· {listing.distanceMiles.toFixed(1)} mi</span>}
            </div>
          </div>

          <p className="text-sm text-gray-700 mb-4 whitespace-pre-line">{listing.description}</p>

          {/* Contact seller — Classified ads are contact-only, no quotes/cart/checkout */}
          {!isSeller && listing.status === ListingStatus.Active && (
            !account ? (
              <div data-testid="sign-in-to-respond" className="border border-blue-200 bg-blue-50 rounded-xl p-4">
                <p className="text-sm font-semibold text-blue-900 mb-1">Want to respond to this ad?</p>
                <p className="text-xs text-blue-700 mb-3">Sign in to view the seller's profile and get in touch.</p>
                <button
                  data-testid="sign-in-to-respond-button"
                  onClick={() => login()}
                  className="w-full py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700"
                >
                  Sign In to Contact
                </button>
              </div>
            ) : (
              <>
                <div data-testid="classified-contact-card" className="border border-gray-200 rounded-xl p-4">
                  <h3 className="text-sm font-bold text-gray-800 mb-2">Contact Seller</h3>
                  {listing.contactPhone && (
                    <p className="text-sm text-gray-600 mb-1"><span className="font-medium">Phone:</span> {listing.contactPhone}</p>
                  )}
                  {listing.contactEmail && (
                    <p className="text-sm text-gray-600"><span className="font-medium">Email:</span> {listing.contactEmail}</p>
                  )}
                </div>
                <button
                  data-testid="listing-add-to-favourites-button"
                  onClick={() => favouriteMutation.mutate()}
                  disabled={favouriteMutation.isPending}
                  className="w-full mt-2 py-2 rounded-lg border border-gray-300 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50 flex items-center justify-center gap-1.5"
                >
                  <Heart className="w-4 h-4" />
                  Add to favourites
                </button>
              </>
            )
          )}

          {isSeller && listing.status === ListingStatus.Active && (
            <div className="flex gap-3 mt-3">
              <button
                data-testid="listing-mark-sold-button"
                onClick={() => soldMutation.mutate()}
                disabled={soldMutation.isPending}
                className="flex-1 py-2 rounded-lg border border-gray-300 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                {soldMutation.isPending ? 'Updating…' : 'Mark as Sold'}
              </button>
              <button
                data-testid="cancel-listing-button"
                onClick={() => cancelMutation.mutate()}
                disabled={cancelMutation.isPending}
                className="flex-1 py-2 rounded-lg border border-red-200 text-sm font-semibold text-red-600 hover:bg-red-50 disabled:opacity-50"
              >
                Cancel Ad
              </button>
            </div>
          )}
        </div>
      </div>

      {sellerReviews && sellerReviews.count > 0 && (
        <div className="mt-8">
          <h2 className="text-base font-bold text-gray-900 mb-1">Seller Reviews</h2>
          <p className="text-xs text-gray-500 mb-3">
            {sellerReviews.positivePercent}% positive · {sellerReviews.count} review{sellerReviews.count === 1 ? '' : 's'}
            {sellerReviews.averageRating != null && ` · ${sellerReviews.averageRating.toFixed(1)} avg rating`}
          </p>
          <div data-testid="listing-seller-reviews" className="space-y-3">
            {sellerReviews.reviews.map(r => (
              <div key={r.reviewId} className="bg-white border border-gray-200 rounded-xl p-3 text-sm">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-semibold text-gray-900">{r.reviewerName}</span>
                  <span className="text-amber-500 text-xs">{'★'.repeat(r.rating)}{'☆'.repeat(5 - r.rating)}</span>
                </div>
                {r.comment && <p className="text-gray-600 text-xs mb-1">{r.comment}</p>}
                <p className="text-gray-400 text-xs">{r.listingTitle} · {new Date(r.createdAt).toLocaleDateString()}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  3. CREATE LISTING PAGE — branches between Bazaar and Classified on submit
// ─────────────────────────────────────────────────────────────────────────────
const CONDITIONS = ['New', 'Like New', 'Good', 'Fair', 'For Parts'];

type DeliveryChoice = 'collection' | 'included' | 'extra';

export function CreateListingPage() {
  const { getToken, account } = useAppAuth();
  const navigate     = useNavigate();
  const [searchParams] = useSearchParams();

  type PostKind = 'sell' | 'classified';
  const initialKind: PostKind = searchParams.get('type') === 'classified' ? 'classified' : 'sell';
  const [postKind, setPostKind] = useState<PostKind>(initialKind);
  const [submitting, setSub]  = useState(false);
  const [error, setError]     = useState<string | null>(null);
  const [media, setMediaList] = useState<ListingMedia[]>([]);
  const [locationTouched, setLocationTouched] = useState(false);

  const [deliveryChoice, setDeliveryChoice] = useState<DeliveryChoice>('collection');
  const [deliveryPrice, setDeliveryPriceState] = useState('');

  const [aiKeywords, setAiKeywords] = useState('');
  const [showAiSuggest, setShowAiSuggest] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiFilledCategory, setAiFilledCategory] = useState(false);

  // Category-specific attribute values, keyed by AttrKey. aiFilledKeys tracks which of them
  // arrived from a suggestion and have not been edited since - that mark is what stops a
  // later "Fill remaining with AI" run touching a field the seller has since corrected.
  const [attrValues, setAttrValues] = useState<Record<string, string>>({});
  const [aiFilledKeys, setAiFilledKeys] = useState<Set<string>>(new Set());
  const [attrErrors, setAttrErrors] = useState<Record<string, string>>({});
  const [droppedNotice, setDroppedNotice] = useState<string | null>(null);

  const [form, setForm] = useState<Partial<CreateBazaarListingInput & CreateClassifiedListingInput>>({
    listingType:             BazaarListingType.Sell,
    pricingType:             BazaarPricingType.Closed,
    intentionPrice:          0,
    mediaUrls:               [],
    responsibilityConfirmed: false,
    hideOfferCount:          false,
  });

  const client = createApiClient(getToken);

  // Default the location field to the user's own profile address, only while the
  // user hasn't typed anything themselves.
  const { data: profileForDefaults } = useQuery({
    queryKey: ['profile', 'me-for-listing-default'],
    queryFn: () => profileService(client).getProfile(),
    enabled: !!account,
  });

  const { data: categories } = useQuery({
    queryKey: ['bazaar', 'categories'],
    queryFn: () => bazaarCategoryService(client).getTree(),
    enabled: !!account,
    staleTime: 5 * 60_000,
  });
  // Subcategories under the currently selected flat CATEGORIES value, matched by name —
  // the old category string and the new taxonomy aren't linked by id, only by label.
  const matchedParent = categories?.find(c => !c.parentId && c.name === form.category);
  const subcategories  = matchedParent ? categories?.filter(c => c.parentId === matchedParent.id) : [];

  // The effective taxonomy node: an explicitly chosen subcategory wins, otherwise the parent
  // matched by name. Everything category-specific hangs off this one value.
  const effectiveCategoryId = form.categoryId ?? matchedParent?.id;

  const { data: attributeDefs, isLoading: attributesLoading } = useQuery({
    queryKey: ['listing-categories', effectiveCategoryId, postKind],
    queryFn: () => bazaarCategoryService(client)
      .getAttributes(effectiveCategoryId!, postKind === 'sell' ? 'bazaar' : 'classified'),
    enabled: !!effectiveCategoryId,
    staleTime: 5 * 60_000,
  });
  const definitions = attributeDefs ?? [];

  // Switching category keeps any value whose key the new category also defines - Vans to
  // Cars keeps Make/Model/Year, Vans to Furniture keeps none - and says so, because losing
  // input the seller typed without telling them would not be acceptable.
  useEffect(() => {
    if (definitions.length === 0) return;
    const allowed = new Set(definitions.map(d => d.attrKey));
    const dropped = Object.keys(attrValues).filter(k => !allowed.has(k) && attrValues[k]?.trim());
    if (dropped.length === 0) return;

    setAttrValues(v => Object.fromEntries(
      Object.entries(v).filter(([k]) => allowed.has(k))));
    setAiFilledKeys(prev => new Set([...prev].filter(k => allowed.has(k))));
    setDroppedNotice(
      dropped.length + ' detail' + (dropped.length === 1 ? '' : 's') +
      ' did not apply to this category and ' +
      (dropped.length === 1 ? 'was' : 'were') + ' removed.');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [definitions]);

  const setAttr = (key: string, value: string) => {
    setAttrValues(v => ({ ...v, [key]: value }));
    // Editing an AI-filled field clears its badge and locks it against later AI runs.
    setAiFilledKeys(prev => {
      if (!prev.has(key)) return prev;
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
    setAttrErrors(e => (e[key] ? { ...e, [key]: '' } : e));
  };

  useEffect(() => {
    if (locationTouched || !profileForDefaults) return;
    const defaultLocation = profileForDefaults.city || profileForDefaults.addressLine1;
    if (defaultLocation && !form.location) {
      setForm(f => ({ ...f, location: defaultLocation, postcode: profileForDefaults.postcode }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileForDefaults]);

  if (!account) return null;

  const set = (patch: Partial<CreateBazaarListingInput & CreateClassifiedListingInput>) => setForm(f => ({ ...f, ...patch }));

  const uploadSvc = postKind === 'sell' ? bazaarService(client) : classifiedService(client);
  const aiSvc     = postKind === 'sell' ? bazaarService(client) : classifiedService(client);

  const handleUpload = async (files: File[]) => {
    const uploaded = await uploadSvc.uploadMedia(files);
    setMediaList(m => [...m, ...uploaded]);
    return uploaded;
  };

  const handleRemoveMedia = (url: string) => setMediaList(m => m.filter(x => x.url !== url));

  const handleAiSuggest = async () => {
    const hasImages = media.length > 0;
    const hasKeywords = aiKeywords.trim().length > 0;
    if (!hasImages && !hasKeywords) {
      setError('Upload a photo or enter a few keywords so AI can help.');
      return;
    }
    setError(null);
    setAiLoading(true);
    try {
      const result = await aiSvc.aiSuggest({
        category: form.category || undefined,
        keywords: aiKeywords.trim() || undefined,
        condition: form.condition || undefined,
        imageUrls: media.filter(m => m.type === 'image').map(m => m.url),
        allowedCategories: topLevelNames(categories),
        // The draft goes up as ground truth. The description in particular is an INPUT -
        // "2019 Ford Transit, 58k miles" is where make/model/year/mileage get read from
        // when there is no usable photo.
        title: form.title || undefined,
        description: form.description || undefined,
        categoryId: effectiveCategoryId,
        attributes: toAttributeValues(attrValues, aiFilledKeys),
      });

      // MERGE, never assign: the API returns null for anything the seller already wrote, and
      // assigning that null straight into state would blank a field they had filled in.
      set({
        title: form.title || result.title || '',
        description: form.description || result.description || '',
        category: form.category || result.category || form.category,
      });
      if (!form.category && result.category) setAiFilledCategory(true);

      const suggested = result.attributes ?? [];
      if (suggested.length > 0) {
        setAttrValues(v => {
          const next = { ...v };
          for (const a of suggested) if (!next[a.key]?.trim()) next[a.key] = a.value;
          return next;
        });
        setAiFilledKeys(prev => {
          const next = new Set(prev);
          for (const a of suggested) if (!attrValues[a.key]?.trim()) next.add(a.key);
          return next;
        });
      }

      setShowAiSuggest(false);
      setAiKeywords('');
    } catch {
      setError('AI Suggest failed — please try again or fill the fields manually.');
    } finally {
      setAiLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!form.title?.trim())    { setError('Title is required.'); return; }
    if (!form.description?.trim()) { setError('Description is required.'); return; }
    if (!form.category?.trim()) { setError('Category is required.'); return; }
    if (!form.location?.trim()) { setError('Location is required.'); return; }
    if (deliveryChoice === 'extra' && (!deliveryPrice || Number(deliveryPrice) <= 0)) {
      setError('Please enter a delivery price, or choose "Included in price" instead.'); return;
    }
    if (!form.responsibilityConfirmed) {
      setError('Please confirm the seller responsibility statement.'); return;
    }

    // The server enforces this too (and is the authority); checking here saves a round trip
    // and puts the message next to the field rather than only at the top of the form.
    const missingAttrs = definitions.filter(d => d.isRequired && !attrValues[d.attrKey]?.trim());
    if (missingAttrs.length > 0) {
      setAttrErrors(Object.fromEntries(
        missingAttrs.map(d => [d.attrKey, d.label + ' is required.'])));
      setError('This category needs ' + missingAttrs.map(d => d.label).join(', ') + '.');
      return;
    }

    const deliveryOption = deliveryChoice === 'collection' ? DeliveryOption.BuyerArranges : DeliveryOption.IncludesDelivery;
    const finalDeliveryPrice = deliveryChoice === 'extra' ? Number(deliveryPrice) : deliveryChoice === 'included' ? 0 : undefined;
    const mediaUrls = media.map(m => m.url);

    setSub(true);
    try {
      if (postKind === 'sell') {
        await bazaarService(client).create({
          listingType: BazaarListingType.Sell,
          title: form.title!.trim(),
          description: form.description!.trim(),
          category: form.category!,
          categoryId: form.categoryId,
          condition: form.condition,
          intentionPrice: form.intentionPrice ?? 0,
          pricingType: form.pricingType ?? BazaarPricingType.Closed,
          deliveryOption,
          deliveryPrice: finalDeliveryPrice,
          location: form.location!.trim(),
          postcode: form.postcode,
          mediaUrls,
          responsibilityConfirmed: !!form.responsibilityConfirmed,
          hideOfferCount: !!form.hideOfferCount,
          attributes: toAttributeValues(attrValues, aiFilledKeys),
        });
        navigate('/bazaar/mine');
      } else {
        const result = await classifiedService(client).create({
          title: form.title!.trim(),
          description: form.description!.trim(),
          category: form.category!,
          condition: form.condition,
          intentionPrice: form.intentionPrice ?? 0,
          deliveryOption,
          deliveryPrice: finalDeliveryPrice,
          location: form.location!.trim(),
          postcode: form.postcode,
          coverageArea: form.coverageArea,
          coverageDetail: form.coverageDetail,
          mediaUrls,
          responsibilityConfirmed: !!form.responsibilityConfirmed,
          categoryId: effectiveCategoryId,
          attributes: toAttributeValues(attrValues, aiFilledKeys),
        });
        navigate(`/classifieds/checkout/${result.listingId}`, {
          state: { clientSecret: result.clientSecret, feeAmount: result.feeAmount },
        });
      }
    } catch (err: any) {
      setError(err?.message ?? 'Failed to create listing. Please try again.');
    } finally {
      setSub(false);
    }
  };

  const TYPE_OPTS: { value: PostKind; label: string; desc: string }[] = [
    { value: 'sell',       label: 'Sell',        desc: 'List an item you want to sell' },
    { value: 'classified', label: 'Classified',  desc: 'Services, jobs, announcements' },
  ];

  return (
    <div data-testid="create-listing-screen" className="page-shell-tight">
      <Link to="/bazaar" className="flex items-center gap-1 text-sm text-blue-600 hover:underline mb-4">
        <ArrowLeft className="w-4 h-4" /> Back to Bazaar
      </Link>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Post a Listing</h1>

      <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
        {/* ── Left column: media + AI ─────────────────────────────────── */}
        <div className="space-y-6 lg:sticky lg:top-6">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Photos & videos</label>
            <MediaUploader media={media} onUpload={handleUpload} onRemove={handleRemoveMedia} testId="create-listing-media" />
          </div>

          <div className="bg-violet-50 border border-violet-200 rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-violet-700" />
                <span className="text-sm font-bold text-violet-800">AI Assist</span>
              </div>
              <button
                type="button"
                data-testid="ai-suggest-toggle"
                onClick={() => setShowAiSuggest(v => !v)}
                className="text-xs font-semibold text-white bg-violet-700 rounded-lg px-3 py-1.5 hover:bg-violet-800"
              >
                {showAiSuggest ? 'Hide' : 'Open'}
              </button>
            </div>
            {/* The copy has to state the guarantee, because the whole reason a seller trusts
                this button is that pressing it cannot cost them work they have already done. */}
            <p className="text-xs text-violet-700 mb-2">
              {media.some(m => m.type === 'image')
                ? 'AI reads your photos and description to fill whatever you have left blank. It never changes anything you have already typed.'
                : 'Write a description or add keywords (or upload a photo above) and AI fills the rest. It never changes anything you have already typed.'}
            </p>
            {!!effectiveCategoryId && definitions.length > 0 && (
              <p data-testid="ai-suggest-remaining" className="text-xs text-violet-600 mb-2 font-medium">
                {countRemaining(definitions, attrValues)} of {definitions.length} category details still empty
              </p>
            )}
            {showAiSuggest && (
              <div data-testid="ai-suggest-panel" className="space-y-2">
                <input
                  data-testid="ai-keywords-input"
                  value={aiKeywords}
                  onChange={e => setAiKeywords(e.target.value)}
                  placeholder="Optional keywords (colour, brand, year…)"
                  className="w-full border border-violet-200 rounded-lg px-3 py-2 text-sm"
                />
                <button
                  type="button"
                  data-testid="ai-suggest-generate"
                  onClick={handleAiSuggest}
                  disabled={aiLoading}
                  className="flex items-center gap-1.5 bg-violet-700 text-white text-xs font-semibold rounded-lg px-4 py-2 hover:bg-violet-800 disabled:opacity-50"
                >
                  {/* "Fill remaining", not "Generate": it is safe to press repeatedly because
                      it only ever touches blanks, so it is the control you reach for after
                      each photo or edit rather than a one-shot at the start. */}
                  <Sparkles className="w-3.5 h-3.5" /> {aiLoading ? 'Filling…' : 'Fill remaining with AI'}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* ── Right column: form fields ───────────────────────────────── */}
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Listing type *</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {TYPE_OPTS.map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  data-testid={`listing-type-${opt.label.toLowerCase().replace(/ /g, '-')}`}
                  onClick={() => setPostKind(opt.value)}
                  className={`text-left p-3 rounded-xl border-2 transition-colors ${
                    postKind === opt.value ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <p className="text-sm font-semibold text-gray-900">{opt.label}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{opt.desc}</p>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
            <input
              data-testid="create-listing-title-input"
              value={form.title ?? ''}
              onChange={e => set({ title: e.target.value })}
              placeholder="e.g. Ford Transit 2019 for sale"
              maxLength={150}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
            <textarea
              data-testid="create-listing-description-input"
              value={form.description ?? ''}
              onChange={e => set({ description: e.target.value })}
              rows={4}
              placeholder="Describe your item or service in detail…"
              maxLength={5000}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category * {aiFilledCategory && form.category && <span className="text-violet-600 font-normal">(AI-selected)</span>}
              </label>
              <select
                data-testid="create-listing-category-select"
                value={form.category ?? ''}
                onChange={e => { set({ category: e.target.value, categoryId: undefined }); setAiFilledCategory(false); }}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select category</option>
                {topLevelNames(categories).map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            {/* Classifieds get the taxonomy too — the subcategory is what decides which
                attributes an ad is asked for, so gating it to Bazaar would leave every
                Classified ad on the parent category's attribute set. */}
            {!!subcategories?.length && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subcategory (optional)</label>
                <select
                  data-testid="create-listing-subcategory-select"
                  value={form.categoryId ?? ''}
                  onChange={e => set({ categoryId: e.target.value || undefined })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">None</option>
                  {subcategories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            )}
            {postKind === 'sell' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Condition</label>
                <select
                  data-testid="create-listing-condition-select"
                  value={form.condition ?? ''}
                  onChange={e => set({ condition: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select condition</option>
                  {CONDITIONS.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            )}
          </div>

          {/* Category-specific details. Appears as soon as a category is chosen and swaps
              wholesale when a different one is; renders nothing for a category that defines
              no attributes, rather than an empty panel. */}
          {droppedNotice && (
            <div
              data-testid="create-listing-attributes-dropped"
              className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800"
            >
              <span className="flex-1">{droppedNotice}</span>
              <button
                type="button"
                onClick={() => setDroppedNotice(null)}
                className="text-amber-700 hover:underline text-xs"
              >
                Dismiss
              </button>
            </div>
          )}

          {!!effectiveCategoryId && (
            <ListingAttributeFields
              definitions={definitions}
              values={attrValues}
              aiFilledKeys={aiFilledKeys}
              errors={attrErrors}
              loading={attributesLoading}
              onChange={setAttr}
            />
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Price (£) *</label>
              <input
                data-testid="create-listing-price-input"
                type="number"
                min="0"
                step="0.01"
                value={form.intentionPrice || ''}
                onChange={e => set({ intentionPrice: Number(e.target.value) })}
                placeholder="0.00"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            {postKind === 'sell' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Pricing type</label>
                <select
                  data-testid="create-listing-pricing-type-select"
                  value={form.pricingType ?? BazaarPricingType.Closed}
                  onChange={e => set({ pricingType: Number(e.target.value) as BazaarPricingType })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value={BazaarPricingType.Closed}>Fixed price</option>
                  <option value={BazaarPricingType.Open}>Open to offers</option>
                </select>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Delivery</label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {([
                { value: 'collection' as const, label: 'Collection only', desc: "Buyer collects — we'll offer a platform delivery link" },
                { value: 'included' as const,   label: 'Delivery included', desc: 'Free, bundled in your price' },
                { value: 'extra' as const,      label: 'Delivery at a price', desc: 'You set an extra delivery charge' },
              ]).map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  data-testid={`delivery-choice-${opt.value}`}
                  onClick={() => setDeliveryChoice(opt.value)}
                  className={`text-left p-3 rounded-xl border-2 transition-colors ${
                    deliveryChoice === opt.value ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <p className="text-xs font-semibold text-gray-900">{opt.label}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{opt.desc}</p>
                </button>
              ))}
            </div>
            {deliveryChoice === 'extra' && (
              <input
                data-testid="create-listing-delivery-price-input"
                type="number"
                min="0.01"
                step="0.01"
                value={deliveryPrice}
                onChange={e => setDeliveryPriceState(e.target.value)}
                placeholder="Delivery charge (£)"
                className="w-full mt-2 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Location *</label>
            <input
              data-testid="create-listing-location-input"
              value={form.location ?? ''}
              onChange={e => { set({ location: e.target.value }); setLocationTouched(true); }}
              placeholder="e.g. London, Manchester"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-gray-400 mt-1">Defaults to your profile address — edit if you'd like to list somewhere else.</p>
          </div>

          {postKind === 'sell' && (
            <div className="flex items-start gap-3">
              <input
                id="hide-offer-count"
                data-testid="create-listing-hide-offer-count-checkbox"
                type="checkbox"
                checked={!!form.hideOfferCount}
                onChange={e => set({ hideOfferCount: e.target.checked })}
                className="mt-0.5 w-4 h-4 accent-blue-600"
              />
              <label htmlFor="hide-offer-count" className="text-xs text-gray-600 leading-snug">
                Hide the number of offers from other buyers (you'll still see all offers yourself).
              </label>
            </div>
          )}

          <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4">
            <input
              id="responsibility"
              data-testid="create-listing-responsibility-checkbox"
              type="checkbox"
              checked={!!form.responsibilityConfirmed}
              onChange={e => set({ responsibilityConfirmed: e.target.checked })}
              className="mt-0.5 w-4 h-4 accent-blue-600"
            />
            <label htmlFor="responsibility" className="text-xs text-amber-900 leading-snug">
              I confirm that I am the legal owner of this item, that it is accurately described, and that I take
              full responsibility for the {postKind === 'sell' ? 'sale' : 'listing'}. TransitDeal acts as a
              marketplace only and does not own or warrant any listed goods.
            </label>
          </div>

          {error && (
            <div className="text-red-600 text-sm bg-red-50 border border-red-200 rounded-lg px-4 py-3">{error}</div>
          )}

          <button
            data-testid="create-listing-submit-button"
            type="submit"
            disabled={submitting}
            className="w-full py-3 bg-blue-600 text-white rounded-xl text-sm font-bold hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {submitting ? 'Posting listing…' : 'Post Listing'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  4. MY BAZAAR PAGE (listings + orders — Classified ads are managed via
//     Classifieds browse + detail, mirroring the mobile app's scope decision)
// ─────────────────────────────────────────────────────────────────────────────
export function MyBazaarPage() {
  const { getToken, account } = useAppAuth();
  const qc           = useQueryClient();
  const [tab, setTab] = useState<'listings' | 'orders' | 'sales'>('listings');
  const [codError, setCodError] = useState<string | null>(null);
  const [otpInputs, setOtpInputs] = useState<Record<string, string>>({});
  const [otpReveal, setOtpReveal] = useState<Record<string, string>>({});
  const [otpRevealError, setOtpRevealError] = useState<Record<string, string>>({});

  const client = createApiClient(getToken);
  const svc    = bazaarService(client);
  const ordersSvc = bazaarOrdersService(client);

  const { data: listings = [], isLoading: loadingListings } = useQuery({
    queryKey: ['bazaar', 'mine'],
    queryFn:  () => svc.getMine(),
    enabled: !!account,
  });

  const { data: orders = [], isLoading: loadingOrders } = useQuery({
    queryKey: ['bazaar', 'orders', 'mine'],
    queryFn:  () => svc.getMyOrders(),
    enabled: !!account,
  });

  // account.userId is the identity provider's own user id, not this app's UserId — same gap
  // documented on BazaarListingDetailPage above — so resolving "am I the seller on this order"
  // needs the real backend UserId, not the MSAL account id.
  const { data: myProfile } = useQuery({
    queryKey: ['profile', 'me-for-delivery-link'],
    queryFn:  () => profileService(createApiClient(getToken)).getProfile(),
    enabled:  !!account,
  });

  const { data: payoutStatus } = useQuery({
    queryKey: ['stripe-connect', 'status'],
    queryFn: async () => stripeConnectApi.getOnboardingStatus(await getToken()),
    enabled: !!account && tab === 'sales',
  });

  const onboardMutation = useMutation({
    mutationFn: async () => stripeConnectApi.startExpressOnboarding(await getToken()),
    onSuccess: (result) => { window.location.href = result.onboardingUrl; },
  });

  const cancelMutation = useMutation({
    mutationFn: (id: string) => svc.cancel(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bazaar', 'mine'] }),
  });

  const otpMutation = useMutation({
    mutationFn: (orderId: string) => ordersSvc.getOtp(orderId),
    onSuccess: (result, orderId) => {
      setOtpReveal(prev => ({ ...prev, [orderId]: result.otp }));
      setOtpRevealError(prev => ({ ...prev, [orderId]: '' }));
    },
    onError: (e: any, orderId) =>
      setOtpRevealError(prev => ({ ...prev, [orderId]: e?.response?.data?.error ?? e?.message ?? 'Could not get delivery code.' })),
  });

  const codMutation = useMutation({
    mutationFn: ({ orderId, otpCode }: { orderId: string; otpCode: string }) => ordersSvc.codReceived(orderId, otpCode),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bazaar', 'orders', 'mine'] });
      setCodError(null);
    },
    onError: (e: any) => setCodError(e?.response?.data?.error ?? e?.message ?? 'Failed to confirm COD received.'),
  });

  if (!account) return null;

  const mySoldItems = myProfile
    ? orders.flatMap(o => o.items.filter(i => i.sellerId === myProfile.userId).map(i => ({ order: o, item: i })))
    : [];
  const salesOwed     = mySoldItems.reduce((sum, { item }) => sum + item.depositAmount + item.codAmount, 0);
  const salesReleased = mySoldItems.filter(({ item }) => !!item.paymentReleasedAt).reduce((sum, { item }) => sum + item.depositAmount, 0);
  const salesCodDue   = mySoldItems.filter(({ order }) => order.status !== BazaarOrderStatus.Completed).reduce((sum, { item }) => sum + item.codAmount, 0);

  return (
    <div data-testid="my-bazaar-screen" className="page-shell-tight">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold text-gray-900">My Bazaar</h1>
        <Link
          to="/bazaar/create?type=sell"
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-blue-700"
        >
          <Plus className="w-4 h-4" /> New Listing
        </Link>
      </div>

      <div className="flex gap-1 bg-gray-100 rounded-xl p-1 mb-6 w-fit">
        {(['listings', 'orders', 'sales'] as const).map(t => (
          <button
            key={t}
            data-testid={`my-bazaar-tab-${t}`}
            onClick={() => setTab(t)}
            className={`px-4 py-1.5 rounded-lg text-sm font-semibold capitalize transition-colors ${
              tab === t ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === 'listings' && (
        loadingListings ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full" />
          </div>
        ) : listings.length === 0 ? (
          <div data-testid="my-listings-empty" className="text-center py-16 text-gray-400">
            <Tag className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p className="font-semibold">No listings yet</p>
            <p className="text-sm mt-1">Post your first listing to get started</p>
          </div>
        ) : (
          <div data-testid="my-listings-list" className="space-y-3">
            {listings.map(l => (
              <div key={l.id} data-testid={`my-listing-item-${l.id}`} className="bg-white border border-gray-200 rounded-xl p-4 flex items-center gap-4">
                {l.media[0] && (
                  <img src={l.media[0].url} alt={l.title} className="w-16 h-16 rounded-lg object-cover shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <Link to={`/bazaar/${l.id}`} className="text-sm font-semibold text-gray-900 hover:text-blue-600 truncate block">
                    {l.title}
                  </Link>
                  <p className="text-xs text-gray-500 mt-0.5">{l.category}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${STATUS_COLORS[l.status]}`}>
                      {STATUS_LABELS[l.status]}
                    </span>
                    <span className="text-sm font-bold text-blue-700">
                      {l.pricingType === BazaarPricingType.Open ? 'Open' : `£${l.intentionPrice.toFixed(2)}`}
                    </span>
                    {l.quoteCount > 0 && (
                      <span className="text-xs text-gray-400">{l.quoteCount} quote{l.quoteCount > 1 ? 's' : ''}</span>
                    )}
                  </div>
                </div>
                <div className="flex flex-col gap-1.5 shrink-0">
                  <Link
                    to={`/bazaar/${l.id}`}
                    className="px-3 py-1 text-xs font-semibold text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-50"
                  >
                    View
                  </Link>
                  {l.status === ListingStatus.Active && (
                    <button
                      data-testid={`cancel-listing-${l.id}`}
                      onClick={() => cancelMutation.mutate(l.id)}
                      disabled={cancelMutation.isPending}
                      className="px-3 py-1 text-xs font-semibold text-red-600 border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-50"
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {tab === 'orders' && (
        loadingOrders ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full" />
          </div>
        ) : orders.length === 0 ? (
          <div data-testid="my-orders-empty" className="text-center py-16 text-gray-400">
            <ShoppingCart className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p className="font-semibold">No orders yet</p>
            <p className="text-sm mt-1">Your purchases will appear here</p>
          </div>
        ) : (
          <div data-testid="my-orders-list" className="space-y-3">
            {(orders as BazaarOrder[]).map(o => (
              <div key={o.id} data-testid={`my-order-item-${o.id}`} className="bg-white border border-gray-200 rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-bold text-gray-900">Order #{o.id.slice(0, 8).toUpperCase()}</p>
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                    o.status === BazaarOrderStatus.Completed ? 'bg-green-100 text-green-800'
                    : o.status === BazaarOrderStatus.Disputed ? 'bg-red-100 text-red-700'
                    : o.status === BazaarOrderStatus.Cancelled ? 'bg-gray-100 text-gray-500'
                    : 'bg-amber-100 text-amber-800'
                  }`}>
                    {ORDER_STATUS_LABELS[o.status]}
                  </span>
                </div>
                {o.items.map((item, i) => (
                  <div key={i} className="py-1 border-b border-gray-50 last:border-0">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-700 truncate flex-1">{item.listingTitle}</span>
                      <span className="font-semibold text-gray-900 ml-4">£{item.totalPrice.toFixed(2)}</span>
                    </div>
                    <BazaarFulfilmentSection order={o} item={item} role="buyer" />
                  </div>
                ))}
                <div className="flex justify-between text-xs text-gray-500 mt-2">
                  <span>Deposit paid: £{o.totalDepositPaid.toFixed(2)}</span>
                  <span>COD due: £{o.totalCodDue.toFixed(2)}</span>
                  {o.cashbackEarned != null && o.cashbackEarned > 0 && (
                    <span className="text-green-600">Cashback: £{o.cashbackEarned.toFixed(2)}</span>
                  )}
                </div>
                {o.status === BazaarOrderStatus.AwaitingCod && !!myProfile && o.buyerId === myProfile.userId && (
                  <div className="mt-3 pt-2 border-t border-gray-100">
                    <button
                      data-testid={`show-otp-${o.id}`}
                      onClick={() => otpMutation.mutate(o.id)}
                      disabled={otpMutation.isPending}
                      className="w-full px-3 py-1.5 text-xs font-semibold text-blue-700 border border-blue-200 bg-blue-50 rounded-lg hover:bg-blue-100 disabled:opacity-50"
                    >
                      Show delivery code
                    </button>
                    {otpReveal[o.id] && (
                      <p data-testid={`order-otp-${o.id}`} className="text-sm font-bold text-blue-800 text-center mt-2">
                        Your code: {otpReveal[o.id]}
                      </p>
                    )}
                    {otpRevealError[o.id] && (
                      <p className="text-xs text-red-600 mt-1">{otpRevealError[o.id]}</p>
                    )}
                  </div>
                )}
                {o.status === BazaarOrderStatus.AwaitingCod
                  && !!myProfile
                  && o.items.some(item => item.sellerId === myProfile.userId) && (
                  <div className="mt-3 pt-2 border-t border-gray-100 flex gap-2">
                    <input
                      data-testid={`cod-otp-input-${o.id}`}
                      value={otpInputs[o.id] ?? ''}
                      onChange={e => setOtpInputs(prev => ({ ...prev, [o.id]: e.target.value }))}
                      placeholder="Delivery code"
                      className="flex-1 min-w-0 border border-gray-300 rounded-lg px-2 py-1.5 text-xs"
                    />
                    <button
                      data-testid={`confirm-cod-${o.id}`}
                      onClick={() => codMutation.mutate({ orderId: o.id, otpCode: otpInputs[o.id] ?? '' })}
                      disabled={codMutation.isPending || !otpInputs[o.id]}
                      className="shrink-0 px-3 py-1.5 text-xs font-semibold text-white bg-emerald-700 rounded-lg hover:bg-emerald-800 disabled:opacity-50"
                    >
                      Confirm COD Received
                    </button>
                  </div>
                )}
                {codError && codMutation.variables?.orderId === o.id && (
                  <p className="text-xs text-red-600 mt-1">{codError}</p>
                )}
              </div>
            ))}
          </div>
        )
      )}

      {tab === 'sales' && (
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-white border border-gray-200 rounded-xl p-3">
              <p className="text-[10px] font-semibold text-gray-500 uppercase">Total owed</p>
              <p data-testid="sales-total-owed" className="text-lg font-bold text-gray-900">£{salesOwed.toFixed(2)}</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-3">
              <p className="text-[10px] font-semibold text-gray-500 uppercase">Released</p>
              <p data-testid="sales-total-released" className="text-lg font-bold text-emerald-700">£{salesReleased.toFixed(2)}</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-3">
              <p className="text-[10px] font-semibold text-gray-500 uppercase">COD to collect</p>
              <p data-testid="sales-total-cod" className="text-lg font-bold text-amber-700">£{salesCodDue.toFixed(2)}</p>
            </div>
          </div>

          {payoutStatus && !payoutStatus.onboarded && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-center justify-between">
              <p className="text-xs text-amber-800">Connect a payout account so we can pay you when a sale completes.</p>
              <button
                data-testid="sales-connect-payout"
                onClick={() => onboardMutation.mutate()}
                disabled={onboardMutation.isPending}
                className="shrink-0 ml-3 px-3 py-1.5 text-xs font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                Connect payout account
              </button>
            </div>
          )}

          {mySoldItems.length === 0 ? (
            <div data-testid="sales-empty" className="text-center py-16 text-gray-400">
              <ShoppingCart className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p className="font-semibold">No sales yet</p>
            </div>
          ) : (
            <div data-testid="sales-list" className="bg-white border border-gray-200 rounded-xl divide-y divide-gray-100">
              {mySoldItems.map(({ order, item }) => (
                <div key={item.id} data-testid={`sale-item-${item.id}`} className="p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">{item.listingTitle}</p>
                      <p className="text-xs text-gray-500">Order #{order.id.slice(0, 8).toUpperCase()}</p>
                      <div className="flex gap-3 text-xs text-gray-600 mt-1">
                        <span>Online: £{item.depositAmount.toFixed(2)}</span>
                        <span>COD: £{item.codAmount.toFixed(2)}</span>
                      </div>
                    </div>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full shrink-0 ${
                      item.paymentReleasedAt ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'
                    }`}>
                      {item.paymentReleasedAt ? 'Released' : 'Awaiting delivery code'}
                    </span>
                  </div>
                  <BazaarFulfilmentSection order={order} item={item} role="seller" />
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
